import { supabase } from '../supabaseClient';
import { describeError } from '../errorMessage';
import { angemeldeterBenutzer, type AngemeldeterBenutzer } from '../angemeldeterBenutzer';
import {
  AblageFehler,
  entferneDatei,
  findeErzeugteUnterlage,
  istDoppelablage,
  istFehlendeKennzeichnung,
  uploadDokument,
} from '../projektDokumenteService';
import { pruefeDatei } from '../../components/dokumente/dokumenteLogic';
import type { DokumentEntwurf, ErzeugendesModul } from '../../components/dokumente/types';
import { heuteBerlin } from '../dateService';
import { istFehlendesSchema } from './sifaService';
import { mapUnterschriften } from './gbuPublicService';
import { parseIsoDatum } from './fristenLogic';
import { DAUER_MAX_MINUTEN } from './taetigkeitenLogic';
import { ARBMEDVV_ANLAESSE } from './kataloge/arbmedvvAnlaesse';
import { vorsorgeArtVorschlag } from './gbuLogic';
import type {
  FreigabeWeg,
  GbuAnlass,
  GbuFassungInhalt,
  GbuKiAuswahl,
  GbuKiKopfVorschlag,
  GbuKiVorschlagGefaehrdung,
  GbuKopfEntwurf,
  GbuNotfall,
  GbuRolle,
  GbuRollen,
  GbuStatus,
  GbuUnterschrift,
  GbuVoll,
  GefaehrdungEntwurf,
  GefaehrdungsFaktor,
  MassnahmeArt,
  MassnahmeEntwurf,
  MassnahmeVerweis,
  SifaGbu,
  SifaGbuFassung,
  SifaGbuGefaehrdung,
  SifaGbuMassnahme,
  SifaGbuVorsorge,
  UnterschriftRolle,
  VorsorgeArt,
  VorsorgeEntwurf,
  ZeilenQuelle,
} from './gbuTypes';

/**
 * Datenbankzugriff der Gefaehrdungsbeurteilung (GBU) fuer den INNENDIENST —
 * Tabellen `sifa_gbu`, `sifa_gbu_gefaehrdungen`, `sifa_gbu_massnahmen`,
 * `sifa_gbu_vorsorge`, `sifa_gbu_fassungen` (supabase/sifa_gbu.sql, Nr. 167)
 * und die angemeldeten RPCs aus Nr. 168 (supabase/sifa_gbu_rpc.sql). Die
 * oeffentliche Freigabeseite redet NICHT hierueber, sondern ueber
 * gbuPublicService.ts. Vertrag: docs/sifa-gbu-plan.md §4.3, §8, §10, §14.4 C.
 *
 * Muster sifaService.ts / vorlagenService.ts, mit fuenf Riegeln:
 *
 * (a) DIE LAUFENDE NUMMER WIRD NIE VOR EINEM `await` FESTGELEGT. Sie wird in
 *     der Insert-Schleife unmittelbar vor dem Schreiben geholt; der harte
 *     Riegel ist `unique (project_id, nummer)`, bei 23505 neu lesen und
 *     erneut versuchen.
 *
 * (b) OPTIMISTISCHE SPERRE AM KOPF. speichereGbuKopf, fortschreiben und
 *     archiviereGbu schreiben nur `where revision = <gelesene revision>` und
 *     zaehlen sie dabei hoch. Ein zweiter Bearbeiter bekommt
 *     GbuKonfliktError statt eines stillen Ueberschreibens.
 *
 * (c) GEARBEITET WIRD NUR IM ENTWURF. Kopf, Gefaehrdungen, Vorsorge und das
 *     Anlegen/Loeschen von Massnahmen gehen nur bei Status `entwurf`. Eine
 *     freigegebene GBU oeffnet mit `fortschreiben` die naechste Fassung. Die
 *     einzige Ausnahme sind Umsetzung und Wirksamkeit einer Massnahme — die
 *     fallen gerade NACH der Freigabe an (speichereMassnahme).
 *
 * (d) EINGEFRORENES BLEIBT. Fassungen entstehen und enden nur ueber die RPCs
 *     (einfrieren, zurueckziehen, freigeben); der Trigger aus Nr. 168 laesst
 *     nach der Freigabe nur noch `pdf_dokument_id` von null auf einen Wert zu.
 *     `r_vor`/`r_nach` sind generierte Spalten und werden NIE geschrieben.
 *
 * (f) NACH DER ERSTEN FREIGABE ENTFAELLT, WAS GELOESCHT WIRD. Gefaehrdung,
 *     Massnahme und Vorsorge werden dann nicht geloescht, sondern bekommen
 *     `entfaellt_ab_fassung` (entferneKind, stelleWiederHer) — die gueltige
 *     Fassung behaelt ihre Zeilen und Nachweise. Kein anderer Schreibweg
 *     setzt das Feld. Der Kind-Riegel der Datenbank meldet sich als Klartext
 *     „nicht mehr im Entwurf" (werfeKind).
 *
 * (e) GEBUCHT WIRD GENAU EINMAL. markiereTaetigkeitGebucht geht ueber die
 *     RPC `sifa_taetigkeit_gebucht_markieren` (nur `gebucht_am is null`) und
 *     liefert die ids, die DIESER Aufruf markiert hat — nur sie werden
 *     gutgeschrieben (Muster markiereGebucht in sifaService.ts).
 *
 * FAIL-OPEN: solange Nr. 167 nicht gefahren ist, liefert ladeGbuListe eine
 * leere Liste (42P01/PGRST205). Alle anderen Wege werfen — dort ist „ging
 * nicht" die richtige Antwort. Fehler gehen als `Error(describeError(e))`
 * bzw. als GbuKonfliktError/GbuRpcFehler hinaus.
 */

const T_GBU = 'sifa_gbu';
const T_GEFAEHRDUNGEN = 'sifa_gbu_gefaehrdungen';
const T_MASSNAHMEN = 'sifa_gbu_massnahmen';
const T_VORSORGE = 'sifa_gbu_vorsorge';
const T_FASSUNGEN = 'sifa_gbu_fassungen';
const T_TAETIGKEITEN = 'sifa_taetigkeiten';
const T_FRISTEN = 'sifa_fristen';

/** Wie oft der Insert bei Nummernkollision erneut versucht wird. */
const MAX_NUMMER_VERSUCHE = 4;

/**
 * Kennung der GBU-Fassungen in `project_documents.erzeugt_aus`
 * (`ErzeugendesModul` 'gbu'). Den CHECK `project_documents_erzeugt_aus_check`
 * erweitert Nr. 167. Fehlt das auf einer Umgebung, scheitert die Ablage
 * sichtbar mit 23514 (Klartext in ablageKlartext) — die Freigabe selbst
 * bleibt davon unberuehrt.
 */
export const GBU_ERZEUGT_AUS: ErzeugendesModul = 'gbu';

/**
 * Unterordner der GBU-Fassungen in der Kategorie `gefaehrdungen` — getrennt
 * von fremden GBUs der Nachunternehmer. Gleich `ORDNER_JE_MODUL.gbu`
 * (services/unterlagenAblage.ts).
 */
export const GBU_ABLAGE_ORDNER = 'SiFa-Gefährdungsbeurteilungen';

/** Groesste Unterschrift (PNG-DataURL) in Zeichen — gleich der Pruefung der RPCs (wie sifa_submit_signature). */
export const UNTERSCHRIFT_MAX_ZEICHEN = 150000;

const FAKTOREN: readonly GefaehrdungsFaktor[] = [
  'mechanisch', 'elektrisch', 'gefahrstoffe', 'biologisch', 'brand_explosion', 'thermisch',
  'physikalisch', 'arbeitsumgebung', 'physische_belastung', 'psychisch', 'sonstige',
];
const MASSNAHME_ARTEN: readonly MassnahmeArt[] = ['S', 'T', 'O', 'P'];
const VERWEISE: readonly MassnahmeVerweis[] = ['ba', 'unterweisung', 'pruefung', 'vorsorge'];
const VORSORGE_ARTEN: readonly VorsorgeArt[] = ['pflicht', 'angebot', 'wunsch'];
const QUELLEN: readonly ZeilenQuelle[] = ['manuell', 'ki', 'ersatz', 'vorlage', 'import'];
const ANLAESSE: readonly GbuAnlass[] = ['ersterstellung', 'aenderung', 'ereignis', 'regelpruefung'];
const STATUS: readonly GbuStatus[] = ['entwurf', 'in_freigabe', 'freigegeben', 'archiviert'];
const ROLLEN: readonly UnterschriftRolle[] = ['erstellt', 'geprueft', 'freigegeben'];
const NOTFALL_FELDER: ReadonlyArray<keyof GbuNotfall> = ['ersteHilfe', 'brand', 'notruf', 'zugaenge', 'meldung', 'kontakte'];

// ─── Fehler ─────────────────────────────────────────────────────────────────

const KONFLIKT_MELDUNG = 'Diese Gefährdungsbeurteilung wurde inzwischen von jemand anderem geändert. Bitte neu laden.';
const MELDUNG_RPC_FEHLT = 'Datenbank-Erweiterung Nr. 168 (GBU-Freigabe) fehlt — diese Funktion steht erst danach zur Verfügung.';
const MELDUNG_KEIN_RECHT = 'Fehlt die Berechtigung für dieses Projekt?';

/** Die optimistische Sperre griff: ein anderer Stand wurde inzwischen gespeichert. */
export class GbuKonfliktError extends Error {
  constructor(message: string = KONFLIKT_MELDUNG) {
    super(message);
    this.name = 'GbuKonfliktError';
  }
}

/**
 * Eine RPC aus Nr. 168 hat mit `{ ok: false, fehler }` abgesagt. `fehler` ist
 * der Code der Datenbank ('status' | 'eingabe' | 'zugriff' | …), `message`
 * der Klartext fuer die Oberflaeche.
 */
export class GbuRpcFehler extends Error {
  readonly fehler: string;
  constructor(message: string, fehler: string) {
    super(message);
    this.name = 'GbuRpcFehler';
    this.fehler = fehler;
  }
}

const errorCode = (e: unknown): string =>
  typeof e === 'object' && e !== null ? String((e as { code?: unknown }).code ?? '') : '';

/** 23505 = unique_violation, hier: (project_id, nummer) ist schon vergeben. */
const istNummernKollision = (e: unknown): boolean => errorCode(e) === '23505';

/**
 * Eine Fassung haelt die GBU (`on delete restrict`): Postgres meldet das als
 * 23001 (restrict_violation); 23503 (foreign_key_violation) kommt bei
 * `no action` — beides heisst „haengt noch dran".
 */
const istFremdschluesselSperre = (e: unknown): boolean => errorCode(e) === '23001' || errorCode(e) === '23503';

/** 42703 = Spalte fehlt (z. B. `sifa_taetigkeiten.gbu_id` ohne Nr. 167); PGRST204 = dasselbe aus dem Schema-Cache. */
const istFehlendeSpalte = (e: unknown): boolean => errorCode(e) === '42703' || errorCode(e) === 'PGRST204';

/** RPC fehlt: PGRST202 (Schema-Cache) oder 42883 (Signatur passt nicht). */
const istFehlendeRpc = (e: unknown): boolean => istFehlendesSchema(e) || errorCode(e) === '42883';

const werfe = (e: unknown): never => {
  if (istFehlendesSchema(e)) throw new Error('Die Gefährdungsbeurteilungen sind noch nicht eingerichtet (Migration Nr. 167 fehlt).');
  throw new Error(describeError(e));
};

// ─── Mapper snake → camel ───────────────────────────────────────────────────

type Zeile = Record<string, unknown>;

const str = (v: unknown): string => (typeof v === 'string' ? v : v == null ? '' : String(v));
const strOrNull = (v: unknown): string | null => (v == null ? null : str(v));
const num = (v: unknown, fallback: number): number => {
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : fallback;
};
const numOrNull = (v: unknown): number | null => (v == null || v === '' ? null : num(v, 0));
const boolOrNull = (v: unknown): boolean | null => (v == null ? null : v === true || v === 'true');
const arr = <T>(v: unknown): T[] => (Array.isArray(v) ? (v as T[]) : []);
const strListe = (v: unknown): string[] => arr<unknown>(v).map((x) => str(x)).filter(Boolean);
/** Datum/Zeitstempel ohne Umrechnung — die Anzeige formatiert selbst. */
const iso = (v: unknown): string => str(v);

/** Erster gesetzter Wert unter mehreren Schluesseln — jsonb darf snake ODER camel tragen. */
const pick = (row: Zeile, ...keys: string[]): unknown => {
  for (const k of keys) if (row?.[k] !== undefined && row?.[k] !== null) return row[k];
  return undefined;
};

/** jsonb als Objekt; Zeichenketten werden geparst, ein Array liefert seine erste Zeile, alles andere null. */
const alsObjekt = (data: unknown): Zeile | null => {
  if (data == null) return null;
  if (Array.isArray(data)) return data.length > 0 ? alsObjekt(data[0]) : null;
  if (typeof data === 'string') {
    try {
      const parsed = JSON.parse(data);
      return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? (parsed as Zeile) : null;
    } catch {
      return null;
    }
  }
  return typeof data === 'object' ? (data as Zeile) : null;
};

const ausListe = <T extends string>(liste: readonly T[], v: unknown, fallback: T): T =>
  (liste as readonly string[]).includes(str(v)) ? (str(v) as T) : fallback;

const ausListeOderNull = <T extends string>(liste: readonly T[], v: unknown): T | null =>
  (liste as readonly string[]).includes(str(v)) ? (str(v) as T) : null;

export const leererNotfall = (): GbuNotfall => ({ ersteHilfe: '', brand: '', notruf: '', zugaenge: '', meldung: '', kontakte: '' });

const mapNotfall = (v: unknown): GbuNotfall => {
  const o = alsObjekt(v) ?? {};
  return {
    ersteHilfe: str(pick(o, 'ersteHilfe', 'erste_hilfe')),
    brand: str(pick(o, 'brand')),
    notruf: str(pick(o, 'notruf')),
    zugaenge: str(pick(o, 'zugaenge')),
    meldung: str(pick(o, 'meldung')),
    kontakte: str(pick(o, 'kontakte')),
  };
};

const mapRolle = (v: unknown): GbuRolle => {
  const o = alsObjekt(v) ?? {};
  return { name: str(o.name), funktion: str(o.funktion) };
};

export const leereRollen = (): GbuRollen => ({
  erstellt: { name: '', funktion: '' },
  geprueft: null,
  freigegeben: { name: '', funktion: '' },
});

const mapRollen = (v: unknown): GbuRollen => {
  const o = alsObjekt(v) ?? {};
  return {
    erstellt: mapRolle(o.erstellt),
    geprueft: alsObjekt(o.geprueft) ? mapRolle(o.geprueft) : null,
    freigegeben: mapRolle(o.freigegeben),
  };
};

export const mapGbu = (row: Zeile): SifaGbu => ({
  id: str(row.id),
  projectId: str(row.project_id),
  nummer: num(row.nummer, 0),
  bereichId: str(row.bereich_id),
  titel: str(row.titel),
  anlass: ausListe(ANLAESSE, row.anlass, 'ersterstellung'),
  beteiligte: strOrNull(row.beteiligte),
  unterlagen: strOrNull(row.unterlagen),
  vorschriften: strListe(row.vorschriften),
  psaGrundausstattung: strOrNull(row.psa_grundausstattung),
  randbedingungen: strOrNull(row.randbedingungen),
  umwelt: strOrNull(row.umwelt),
  koordination: strOrNull(row.koordination),
  orgMassnahmen: strListe(row.org_massnahmen),
  notfall: mapNotfall(row.notfall),
  rollen: mapRollen(row.rollen),
  status: ausListe(STATUS, row.status, 'entwurf'),
  fassung: num(row.fassung, 1),
  freigegebeneFassung: numOrNull(row.freigegebene_fassung),
  gueltigAb: strOrNull(row.gueltig_ab),
  pruefenBis: strOrNull(row.pruefen_bis),
  revision: num(row.revision, 0),
  vorlageId: strOrNull(row.vorlage_id) || null,
  vorlageFassung: numOrNull(row.vorlage_fassung),
  createdAt: iso(row.created_at),
  updatedAt: iso(row.updated_at),
});

export const mapGefaehrdung = (row: Zeile): SifaGbuGefaehrdung => {
  const wVor = num(row.w_vor, 1);
  const sVor = num(row.s_vor, 1);
  const wNach = num(row.w_nach, 1);
  const sNach = num(row.s_nach, 1);
  return {
    id: str(row.id),
    gbuId: str(row.gbu_id),
    projectId: str(row.project_id),
    sortOrder: num(row.sort_order, 0),
    faktor: ausListe(FAKTOREN, row.faktor, 'sonstige'),
    gefaehrdung: str(row.gefaehrdung),
    folgen: strOrNull(row.folgen),
    wVor,
    sVor,
    wNach,
    sNach,
    // Generierte Spalten; fehlen sie (aeltere Abfrage), wird gerechnet.
    rVor: num(row.r_vor, wVor * sVor),
    rNach: num(row.r_nach, wNach * sNach),
    arbeitsmittelIds: strListe(row.arbeitsmittel_ids),
    stoffIds: strListe(row.stoff_ids),
    vorschriften: strListe(row.vorschriften),
    quelle: ausListe(QUELLEN, row.quelle, 'manuell'),
    entfaelltAbFassung: numOrNull(row.entfaellt_ab_fassung),
    createdAt: iso(row.created_at),
    updatedAt: iso(row.updated_at),
  };
};

export const mapMassnahme = (row: Zeile): SifaGbuMassnahme => ({
  id: str(row.id),
  gefaehrdungId: str(row.gefaehrdung_id),
  gbuId: str(row.gbu_id),
  projectId: str(row.project_id),
  sortOrder: num(row.sort_order, 0),
  art: ausListe(MASSNAHME_ARTEN, row.art, 'O'),
  text: str(row.text),
  psaCode: strOrNull(row.psa_code),
  verweis: ausListeOderNull(VERWEISE, row.verweis),
  verantwortlich: strOrNull(row.verantwortlich),
  termin: strOrNull(row.termin),
  umgesetztGemeldetAm: strOrNull(row.umgesetzt_gemeldet_am),
  umgesetztGemeldetVon: strOrNull(row.umgesetzt_gemeldet_von),
  umgesetztKommentar: strOrNull(row.umgesetzt_kommentar),
  wirksamGeprueftAm: strOrNull(row.wirksam_geprueft_am),
  wirksamKuerzel: strOrNull(row.wirksam_kuerzel),
  wirksam: boolOrNull(row.wirksam),
  wirksamNotiz: strOrNull(row.wirksam_notiz),
  quelle: ausListe(QUELLEN, row.quelle, 'manuell'),
  entfaelltAbFassung: numOrNull(row.entfaellt_ab_fassung),
  createdAt: iso(row.created_at),
  updatedAt: iso(row.updated_at),
});

export const mapVorsorge = (row: Zeile): SifaGbuVorsorge => ({
  id: str(row.id),
  gefaehrdungId: str(row.gefaehrdung_id),
  gbuId: str(row.gbu_id),
  projectId: str(row.project_id),
  anlassCode: str(row.anlass_code),
  messwert: numOrNull(row.messwert),
  einheit: strOrNull(row.einheit),
  artVorschlag: ausListeOderNull(VORSORGE_ARTEN, row.art_vorschlag),
  // Im Zweifel die strengere Art — ein unlesbarer Wert darf nie wie „nur Angebot" aussehen.
  art: ausListe(VORSORGE_ARTEN, row.art, 'pflicht'),
  begruendung: strOrNull(row.begruendung),
  quelle: ausListe(QUELLEN, row.quelle, 'manuell'),
  entfaelltAbFassung: numOrNull(row.entfaellt_ab_fassung),
  createdAt: iso(row.created_at),
  updatedAt: iso(row.updated_at),
});

export const mapFassung = (row: Zeile): SifaGbuFassung => ({
  id: str(row.id),
  gbuId: str(row.gbu_id),
  projectId: str(row.project_id),
  fassung: num(row.fassung, 0),
  inhalt: (alsObjekt(row.inhalt) ?? {}) as unknown as GbuFassungInhalt,
  freigabeWeg: ausListeOderNull<FreigabeWeg>(['digital', 'papier'], row.freigabe_weg),
  unterschriften: arr<GbuUnterschrift>(row.unterschriften),
  pdfDokumentId: strOrNull(row.pdf_dokument_id),
  scanDokumentId: strOrNull(row.scan_dokument_id),
  freigegebenAm: strOrNull(row.freigegeben_am),
  createdAt: iso(row.created_at),
});

// ─── Hilfen ─────────────────────────────────────────────────────────────────

/** Der Firmen-Anzeigename des angemeldeten Profils — best effort, nie blockierend. */
const eigenerFirmenname = async (): Promise<string | null> => {
  try {
    const { data: auth } = await supabase.auth.getUser();
    const userId = auth?.user?.id;
    if (!userId) return null;
    const { data } = await supabase.from('profiles').select('company_name').eq('id', userId).maybeSingle();
    const name = (data as { company_name?: string } | null)?.company_name;
    return name?.trim() ? name.trim() : null;
  } catch {
    return null;
  }
};

/** Der uebergebene Firmenname, sonst der des Profils. */
const firmaFuer = async (companyName: string | null | undefined): Promise<string | null> =>
  leerZuNull(companyName) ?? (await eigenerFirmenname());

/** Leere Zeichenkette → null (Spalten sind NULL-bar; ein '' ist dort eine Luege). */
const leerZuNull = (v: string | null | undefined): string | null => {
  const t = (v ?? '').trim();
  return t ? t : null;
};

/** Getrimmt, ohne Leere, ohne Dubletten (Gross-/Kleinschreibung egal), Reihenfolge bleibt. */
const textListe = (v: ReadonlyArray<string | null | undefined> | null | undefined): string[] => {
  const gesehen = new Set<string>();
  const aus: string[] = [];
  for (const roh of v ?? []) {
    const t = (roh ?? '').replace(/\s+/g, ' ').trim();
    if (!t || gesehen.has(t.toLowerCase())) continue;
    gesehen.add(t.toLowerCase());
    aus.push(t);
  }
  return aus;
};

/** ISO-Datum oder null; ein kaputtes Datum wirft mit Feldname (nie `new Date`). */
const datumOderNull = (v: string | null | undefined, feld: string): string | null => {
  const t = (v ?? '').trim();
  if (!t) return null;
  if (!parseIsoDatum(t)) throw new Error(`Bitte ein gültiges Datum für „${feld}" eingeben.`);
  return t.slice(0, 10);
};

const pflichtDatum = (v: string | null | undefined, feld: string): string => {
  const d = datumOderNull(v, feld);
  if (!d) throw new Error(`Bitte ein gültiges Datum für „${feld}" eingeben.`);
  return d;
};

/** W bzw. S: ganze Zahl 1–3 (Matrix 3 × 3). */
const stufe13 = (v: unknown, feld: string): number => {
  if (typeof v !== 'number' || !Number.isInteger(v) || v < 1 || v > 3) {
    throw new Error(`${feld} muss 1, 2 oder 3 sein.`);
  }
  return v;
};

const sortierung = (v: unknown): number => (typeof v === 'number' && Number.isFinite(v) ? Math.max(0, Math.round(v)) : 0);

const quelleVon = (v: unknown): ZeilenQuelle => {
  if (!(QUELLEN as readonly unknown[]).includes(v)) throw new Error(`Unbekannte Herkunft „${String(v)}".`);
  return v as ZeilenQuelle;
};

/**
 * Bezug, den die Kind-Zeilen brauchen: welche GBU, welches Projekt, welcher
 * Status (Riegel c) — und welche Fassung bzw. ob es schon eine freigegebene
 * gibt (Loeschen wird dann zu „entfallen", Riegel f). Aufrufer uebergeben die
 * SifaGbu.
 */
export type GbuBezug = Pick<SifaGbu, 'id' | 'projectId' | 'status' | 'fassung' | 'freigegebeneFassung'>;

const MELDUNG_NICHT_MEHR_ENTWURF = 'Die GBU ist nicht mehr im Entwurf — bitte neu laden.';

/**
 * SQLSTATEs, mit denen der Kind-Riegel aus Nr. 167/168 abweist (Aenderung
 * ausserhalb des Entwurfs, DELETE nach der ersten Freigabe). Bis Paket A den
 * Code festlegt: P0001 (raise ohne errcode) und 42501 — eine RLS-Absage
 * („row-level security") ist aber KEIN Riegel und behaelt ihren Text.
 */
const KIND_RIEGEL_CODES: readonly string[] = ['P0001', '42501'];

const istKindRiegel = (e: unknown): boolean => {
  if (!KIND_RIEGEL_CODES.includes(errorCode(e))) return false;
  const text = typeof e === 'object' && e !== null ? `${str((e as { message?: unknown }).message)} ${str((e as { details?: unknown }).details)}` : '';
  return !/row-level security/i.test(text);
};

/** Fehler eines Schreibwegs auf Kind-Zeilen: Riegel → Klartext, sonst wie werfe. */
const werfeKind = (e: unknown): never => {
  if (istKindRiegel(e)) throw new Error(MELDUNG_NICHT_MEHR_ENTWURF);
  return werfe(e);
};

const bezugPruefen = (gbu: GbuBezug | null | undefined): { gbuId: string; projectId: string } => {
  const gbuId = (gbu?.id || '').trim();
  const projectId = (gbu?.projectId || '').trim();
  if (!gbuId || !projectId) throw new Error('Keine Gefährdungsbeurteilung gewählt.');
  return { gbuId, projectId };
};

const STATUS_TEXT: Record<GbuStatus, string> = {
  entwurf: 'im Entwurf',
  in_freigabe: 'in Freigabe',
  freigegeben: 'freigegeben',
  archiviert: 'archiviert',
};

/** Riegel (c): nur im Entwurf. Der Satz sagt, was stattdessen zu tun ist. */
const nurImEntwurf = (gbu: GbuBezug): void => {
  if (gbu.status === 'entwurf') return;
  const weiter =
    gbu.status === 'freigegeben' ? 'Für Änderungen bitte fortschreiben (neue Fassung).'
      : gbu.status === 'in_freigabe' ? 'Für Änderungen bitte die Freigabe zurückziehen.'
        : 'Eine archivierte Beurteilung bleibt, wie sie ist.';
  throw new Error(`Diese Gefährdungsbeurteilung ist ${STATUS_TEXT[gbu.status] ?? gbu.status} und damit schreibgeschützt. ${weiter}`);
};

/** Eine GBU frisch aus der Datenbank; null, wenn es sie nicht gibt (oder RLS sie verbirgt). */
const ladeKopf = async (id: string): Promise<SifaGbu | null> => {
  const { data, error } = await supabase.from(T_GBU).select('*').eq('id', id).maybeSingle();
  if (error) return werfe(error);
  return data ? mapGbu(data as Zeile) : null;
};

/**
 * Ruft eine angemeldete RPC aus Nr. 168 und liefert ihre `ok`-Antwort.
 * `{ ok: false, fehler }` wird zu GbuRpcFehler mit dem Satz aus `texte`;
 * eine fehlende Funktion zu einem Klartext statt PGRST202.
 */
const rufeRpc = async (name: string, params: Zeile, texte: Record<string, string>): Promise<Zeile> => {
  const { data, error } = await supabase.rpc(name, params);
  if (error) {
    if (istFehlendeRpc(error)) throw new Error(MELDUNG_RPC_FEHLT);
    return werfe(error);
  }
  const row = alsObjekt(data);
  if (!row) throw new Error('Die Datenbank hat die Aktion nicht bestätigt — bitte neu laden und prüfen.');
  if (row.ok !== true) {
    const fehler = str(row.fehler).trim() || 'unbekannt';
    throw new GbuRpcFehler(texte[fehler] ?? `Die Aktion wurde abgelehnt (${fehler}).`, fehler);
  }
  return row;
};

/** 'zugriff' heisst bei Nr. 168 auch: die GBU bzw. Fassung gibt es nicht (mehr). */
const RPC_TEXTE_GEMEINSAM: Record<string, string> = {
  zugriff: `Kein Zugriff auf diese Gefährdungsbeurteilung, oder sie bzw. die Fassung gibt es nicht mehr — bitte neu laden. ${MELDUNG_KEIN_RECHT}`,
  eingabe: 'Die Angaben wurden abgelehnt — bitte prüfen und erneut versuchen.',
};

/** Hoechstens so viele Unterschriften je Aufruf (sifa_gbu_unterschriften_pruefen, Nr. 168). */
const MAX_UNTERSCHRIFTEN = 10;
/** Laufzeit eines Freigabe-Links in Tagen (sifa_gbu_freigabe_link_anlegen, Nr. 168). */
export const FREIGABE_LINK_MAX_TAGE = 14;

// ═══ Lesen ════════════════════════════════════════════════════════════════

/**
 * Alle GBUs eines Projekts, nach Nummer aufsteigend (Nr. 1, 2, 3 … wie im
 * Muster). Fail-open bei fehlender Tabelle. RLS antwortet mit LEERER Menge.
 */
export const ladeGbuListe = async (projectId: string): Promise<SifaGbu[]> => {
  const pid = (projectId || '').trim();
  if (!pid) return [];
  try {
    const { data, error } = await supabase
      .from(T_GBU)
      .select('*')
      .eq('project_id', pid)
      .order('nummer', { ascending: true });
    if (error) throw error;
    return ((data ?? []) as Zeile[]).map(mapGbu);
  } catch (e) {
    if (istFehlendesSchema(e)) {
      console.warn('sifa_gbu fehlt — Migration Nr. 167 noch nicht gefahren.');
      return [];
    }
    return werfe(e);
  }
};

/**
 * Eine GBU mit allem, was der Editor braucht — fuenf Abfragen parallel:
 * Kopf, Gefaehrdungen und Massnahmen (nach `sort_order`), Vorsorge (nach
 * Anlage), Fassungen (NEUESTE zuerst). Wirft, wenn es die GBU nicht gibt.
 */
export const ladeGbu = async (gbuId: string): Promise<GbuVoll> => {
  const id = (gbuId || '').trim();
  if (!id) throw new Error('Keine Gefährdungsbeurteilung gewählt.');

  const basis = (tabelle: string) => supabase.from(tabelle).select('*').eq('gbu_id', id);
  const zeilen = ({ data, error }: { data: unknown; error: unknown }): Zeile[] => {
    if (error) return werfe(error);
    return (data ?? []) as Zeile[];
  };

  const [kopf, gefaehrdungen, massnahmen, vorsorge, fassungen] = await Promise.all([
    ladeKopf(id),
    basis(T_GEFAEHRDUNGEN).order('sort_order', { ascending: true }).order('created_at', { ascending: true }).then(zeilen),
    basis(T_MASSNAHMEN).order('sort_order', { ascending: true }).order('created_at', { ascending: true }).then(zeilen),
    basis(T_VORSORGE).order('created_at', { ascending: true }).then(zeilen),
    basis(T_FASSUNGEN).order('fassung', { ascending: false }).then(zeilen),
  ]);
  if (!kopf) throw new Error('Diese Gefährdungsbeurteilung wurde nicht gefunden.');

  return {
    gbu: kopf,
    gefaehrdungen: gefaehrdungen.map(mapGefaehrdung),
    massnahmen: massnahmen.map(mapMassnahme),
    vorsorge: vorsorge.map(mapVorsorge),
    fassungen: fassungen.map(mapFassung),
  };
};

// ═══ Kopf ═════════════════════════════════════════════════════════════════

const rolleZuJson = (r: GbuRolle | null | undefined): GbuRolle => ({
  name: (r?.name ?? '').trim(),
  funktion: (r?.funktion ?? '').trim(),
});

const rollenZuJson = (r: GbuRollen | null | undefined): GbuRollen => ({
  erstellt: rolleZuJson(r?.erstellt),
  geprueft: r?.geprueft ? rolleZuJson(r.geprueft) : null,
  freigegeben: rolleZuJson(r?.freigegeben),
});

const notfallZuJson = (n: Partial<GbuNotfall> | null | undefined): GbuNotfall => {
  const aus = leererNotfall();
  for (const k of NOTFALL_FELDER) aus[k] = (n?.[k] ?? '').trim();
  return aus;
};

/** Kopf-Felder → Spalten; nur, was im Patch steht. Pflichtfelder werfen leer. */
const kopfZuZeile = (k: Partial<GbuKopfEntwurf>): Zeile => {
  const z: Zeile = {};
  if (k.bereichId !== undefined) {
    const b = (k.bereichId ?? '').trim();
    if (!b) throw new Error('Bitte einen Arbeitsbereich wählen.');
    z.bereich_id = b;
  }
  if (k.titel !== undefined) {
    const t = (k.titel ?? '').trim();
    if (!t) throw new Error('Bitte einen Titel für die Gefährdungsbeurteilung eingeben.');
    z.titel = t;
  }
  if (k.anlass !== undefined) {
    if (!ANLAESSE.includes(k.anlass)) throw new Error('Bitte einen Anlass wählen.');
    z.anlass = k.anlass;
  }
  if (k.beteiligte !== undefined) z.beteiligte = leerZuNull(k.beteiligte);
  if (k.unterlagen !== undefined) z.unterlagen = leerZuNull(k.unterlagen);
  if (k.vorschriften !== undefined) z.vorschriften = textListe(k.vorschriften);
  if (k.psaGrundausstattung !== undefined) z.psa_grundausstattung = leerZuNull(k.psaGrundausstattung);
  if (k.randbedingungen !== undefined) z.randbedingungen = leerZuNull(k.randbedingungen);
  if (k.umwelt !== undefined) z.umwelt = leerZuNull(k.umwelt);
  if (k.koordination !== undefined) z.koordination = leerZuNull(k.koordination);
  if (k.orgMassnahmen !== undefined) z.org_massnahmen = textListe(k.orgMassnahmen);
  if (k.notfall !== undefined) z.notfall = notfallZuJson(k.notfall);
  if (k.rollen !== undefined) z.rollen = rollenZuJson(k.rollen);
  return z;
};

/** Die HOECHSTE bisher vergebene Nummer + 1, frisch aus der Datenbank (Riegel a). */
const naechsteNummerAusDb = async (projectId: string): Promise<number> => {
  const { data, error } = await supabase
    .from(T_GBU)
    .select('nummer')
    .eq('project_id', projectId)
    .order('nummer', { ascending: false })
    .limit(1)
    .maybeSingle();
  if (error) return werfe(error);
  const hoechste = Number((data as { nummer?: unknown } | null)?.nummer ?? 0);
  return Number.isFinite(hoechste) && hoechste > 0 ? hoechste + 1 : 1;
};

/** Was beim Anlegen gesetzt werden darf: Arbeitsbereich und Titel Pflicht, der Rest optional. */
export type NeueGbu = Partial<GbuKopfEntwurf> &
  Pick<GbuKopfEntwurf, 'bereichId' | 'titel'> &
  Partial<Pick<SifaGbu, 'vorlageId' | 'vorlageFassung'>>;

/**
 * Legt eine GBU als ENTWURF in Fassung 1 an (Revision 0). Nummer in der
 * Schleife unmittelbar vor dem Insert; bei 23505 neu lesen und erneut
 * versuchen (Riegel a). Ohne `rollen` ist „Erstellt" der angemeldete Nutzer.
 */
export const legeGbuAn = async (projectId: string, entwurf: NeueGbu, companyName?: string | null): Promise<SifaGbu> => {
  const pid = (projectId || '').trim();
  if (!pid) throw new Error('Ohne Projekt lässt sich keine Gefährdungsbeurteilung anlegen.');
  if (!entwurf) throw new Error('Bitte Arbeitsbereich und Titel angeben.');

  // Vorgaben NACH dem Entwurf: ein ausdruecklich undefiniertes Feld darf sie nicht aushebeln.
  const felder = kopfZuZeile({
    ...entwurf,
    bereichId: entwurf.bereichId ?? '',
    titel: entwurf.titel ?? '',
    anlass: entwurf.anlass ?? 'ersterstellung',
    vorschriften: entwurf.vorschriften ?? [],
    orgMassnahmen: entwurf.orgMassnahmen ?? [],
    notfall: entwurf.notfall ?? leererNotfall(),
  });

  // Vor der Schleife: hat mit der Nummer nichts zu tun, sonst je Versuch erneut.
  const [company, ich] = await Promise.all([
    firmaFuer(companyName),
    entwurf.rollen ? Promise.resolve(null) : angemeldeterBenutzer(),
  ]);
  if (!entwurf.rollen) {
    const rollen = leereRollen();
    rollen.erstellt.name = (ich?.name ?? '').trim();
    felder.rollen = rollen;
  }

  // Herkunft NUR, wenn gesetzt (Stufe 1b); eine Fassung ohne Vorlage sagt nichts.
  const vorlageId = (entwurf.vorlageId ?? '').trim();
  if (vorlageId) {
    felder.vorlage_id = vorlageId;
    if (typeof entwurf.vorlageFassung === 'number' && Number.isFinite(entwurf.vorlageFassung)) {
      felder.vorlage_fassung = entwurf.vorlageFassung;
    }
  }

  for (let versuch = 1; ; versuch++) {
    const nummer = await naechsteNummerAusDb(pid);
    const zeile: Zeile = {
      ...felder,
      project_id: pid,
      nummer,
      status: 'entwurf',
      fassung: 1,
      revision: 0,
      company_name: company,
    };
    const { data, error } = await supabase.from(T_GBU).insert(zeile).select('*').single();
    if (!error) return mapGbu(data as Zeile);
    if (!istNummernKollision(error) || versuch >= MAX_NUMMER_VERSUCHE) return werfe(error);
    // Naechste Runde: Nummer wird oben frisch geholt.
  }
};

/**
 * Speichert den Kopf einer GBU im ENTWURF (Riegel b und c im UPDATE selbst:
 * `revision = gbu.revision` und `status = 'entwurf'`; neue Revision =
 * alte + 1). Kommt keine Zeile zurueck, wird nachgelesen, damit die Meldung
 * stimmt: nicht gefunden, nicht mehr im Entwurf, anderer Stand
 * (GbuKonfliktError) oder — gleicher Stand, trotzdem nichts — fehlendes
 * Recht. Ein leerer Patch schreibt nichts und gibt `gbu` zurueck.
 */
export const speichereGbuKopf = async (gbu: SifaGbu, aenderung: Partial<GbuKopfEntwurf>): Promise<SifaGbu> => {
  const id = (gbu?.id || '').trim();
  if (!id) throw new Error('Keine Gefährdungsbeurteilung gewählt.');
  if (!Number.isInteger(gbu.revision) || gbu.revision < 0) throw new Error('Der Stand der Gefährdungsbeurteilung ist unbekannt. Bitte neu laden.');
  nurImEntwurf(gbu);
  const zeile = kopfZuZeile(aenderung ?? {});
  if (Object.keys(zeile).length === 0) return gbu;
  zeile.revision = gbu.revision + 1;
  zeile.updated_at = new Date().toISOString();

  const { data, error } = await supabase
    .from(T_GBU)
    .update(zeile)
    .eq('id', id)
    .eq('revision', gbu.revision)
    .eq('status', 'entwurf')
    .select('*');
  if (error) return werfe(error);
  if (data && data.length > 0) return mapGbu(data[0] as Zeile);

  const vorhanden = await ladeKopf(id);
  if (!vorhanden) throw new Error('Diese Gefährdungsbeurteilung wurde nicht gefunden (Speichern).');
  nurImEntwurf(vorhanden);
  if (vorhanden.revision !== gbu.revision) throw new GbuKonfliktError();
  throw new Error(`Die Gefährdungsbeurteilung konnte nicht gespeichert werden. ${MELDUNG_KEIN_RECHT}`);
};

// ═══ Neue Kind-Zeilen ohne Dublette ═══════════════════════════════════════

/**
 * Zusatz fuer `speichere*` ohne `id`: `neueId` ist die vom Aufrufer EINMAL je
 * neuer Zeile erzeugte uuid (crypto.randomUUID()). Damit wird ein Neuversuch
 * nach verlorener Antwort zum Update derselben Zeile statt zu einer Dublette.
 */
export type NeueZeileOptionen = { neueId?: string };

const IST_UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** `opts.neueId` geprueft und kleingeschrieben (die uuid-Spalte liefert klein zurueck); leer → null. */
const neueIdAus = (opts: NeueZeileOptionen | null | undefined): string | null => {
  const v = (opts?.neueId ?? '').trim();
  if (!v) return null;
  if (!IST_UUID.test(v)) throw new Error('Die Kennung der neuen Zeile ist keine gültige uuid — bitte neu laden.');
  return v.toLowerCase();
};

/**
 * 23505 auf der Kennung: die Zeile mit dieser `neueId` gibt es schon (der
 * erste Versuch kam an, nur die Antwort nicht). Auf den drei Kind-Tabellen
 * sind ohnehin nur `id` (Primaerschluessel) und `(id, gbu_id)` eindeutig;
 * nennt die Meldung einen anderen Constraint, gilt das NICHT.
 */
const istIdKollision = (e: unknown): boolean => {
  if (errorCode(e) !== '23505') return false;
  const o = typeof e === 'object' && e !== null ? (e as { message?: unknown; details?: unknown }) : {};
  const name = /unique constraint "([^"]+)"/.exec(`${str(o.message)} ${str(o.details)}`)?.[1];
  return !name || /_pkey$|_id_gbu_uq$/.test(name);
};

// ═══ Gefaehrdungen ════════════════════════════════════════════════════════

/** Gefaehrdung → Spalten. `r_vor`/`r_nach` NIE (generiert, Riegel d). */
const gefaehrdungZuZeile = (e: GefaehrdungEntwurf): Zeile => {
  if (!e) throw new Error('Keine Gefährdung angegeben.');
  if (!FAKTOREN.includes(e.faktor)) throw new Error('Bitte einen Gefährdungsfaktor wählen.');
  return {
    faktor: e.faktor,
    gefaehrdung: (e.gefaehrdung ?? '').trim(),
    folgen: leerZuNull(e.folgen),
    w_vor: stufe13(e.wVor, 'W vorher'),
    s_vor: stufe13(e.sVor, 'S vorher'),
    w_nach: stufe13(e.wNach, 'W nachher'),
    s_nach: stufe13(e.sNach, 'S nachher'),
    arbeitsmittel_ids: textListe(e.arbeitsmittelIds),
    stoff_ids: textListe(e.stoffIds),
    vorschriften: textListe(e.vorschriften),
    quelle: quelleVon(e.quelle),
    sort_order: sortierung(e.sortOrder),
  };
};

/**
 * Legt eine Gefaehrdung an (ohne `id`) oder aendert sie — nur im Entwurf
 * (Riegel c). W und S je 1–3; R rechnet die Datenbank (generierte Spalte).
 */
export const speichereGefaehrdung = async (
  gbu: GbuBezug,
  entwurf: GefaehrdungEntwurf,
  id?: string,
  companyName?: string | null,
  opts?: NeueZeileOptionen,
): Promise<SifaGbuGefaehrdung> => {
  const { gbuId, projectId } = bezugPruefen(gbu);
  nurImEntwurf(gbu);
  const felder = gefaehrdungZuZeile(entwurf);
  const vid = (id ?? '').trim();
  const neueId = vid ? null : neueIdAus(opts);
  if (vid) {
    const { data, error } = await supabase
      .from(T_GEFAEHRDUNGEN)
      .update({ ...felder, updated_at: new Date().toISOString() })
      .eq('id', vid)
      .eq('gbu_id', gbuId)
      .select('*');
    if (error) return werfeKind(error);
    if (!data || data.length === 0) throw new Error('Diese Gefährdung wurde nicht gefunden (Speichern).');
    return mapGefaehrdung(data[0] as Zeile);
  }
  const { data, error } = await supabase
    .from(T_GEFAEHRDUNGEN)
    .insert({ ...(neueId ? { id: neueId } : {}), ...felder, gbu_id: gbuId, project_id: projectId, company_name: await firmaFuer(companyName) })
    .select('*')
    .single();
  if (error) {
    // Die Zeile gibt es schon (Neuversuch nach verlorener Antwort) → normales Update.
    if (neueId && istIdKollision(error)) return speichereGefaehrdung(gbu, entwurf, neueId, companyName);
    return werfeKind(error);
  }
  return mapGefaehrdung(data as Zeile);
};

/** Was „Loeschen" einer Kind-Zeile bewirkt hat: echt geloescht oder ab dieser Fassung entfallen. */
export type GbuLoeschErgebnis = 'geloescht' | 'entfallen';

/**
 * Riegel (f): NACH DER ERSTEN FREIGABE WIRD NICHT GELOESCHT. Die gueltige
 * Fassung (und ihre Umsetzungs- und Wirksamkeitsnachweise, die an den Ids
 * der Maßnahmen haengen) bliebe sonst ohne Zeilen. Hat die GBU eine
 * `freigegebeneFassung`, setzt „Loeschen" `entfaellt_ab_fassung =
 * gbu.fassung` (die Fassung des Entwurfs) — der Trigger aus Nr. 167/168
 * verbietet dann ohnehin jedes DELETE. Sonst wird echt geloescht. Nur im
 * Entwurf. Eine Gefaehrdung nimmt ihre Massnahmen und Vorsorge dabei NICHT
 * mit: sie gelten als entfallen, solange ihre Gefaehrdung entfallen ist
 * (wiederherstellen bringt alles zurueck).
 */
const entferneKind = async (tabelle: string, gbu: GbuBezug, id: string, was: string): Promise<GbuLoeschErgebnis> => {
  const { gbuId } = bezugPruefen(gbu);
  nurImEntwurf(gbu);
  const vid = (id || '').trim();
  if (!vid) throw new Error(`${was} wurde nicht gefunden.`);

  if (gbu.freigegebeneFassung != null) {
    const fassung = gbu.fassung;
    if (!Number.isInteger(fassung) || fassung < 1) throw new Error('Die Fassung der Gefährdungsbeurteilung ist unbekannt. Bitte neu laden.');
    const { data, error } = await supabase
      .from(tabelle)
      .update({ entfaellt_ab_fassung: fassung, updated_at: new Date().toISOString() })
      .eq('id', vid)
      .eq('gbu_id', gbuId)
      .select('id');
    if (error) return werfeKind(error);
    if (!data || data.length === 0) throw new Error(`${was} wurde nicht gefunden (Löschen).`);
    return 'entfallen';
  }

  const { error } = await supabase.from(tabelle).delete().eq('id', vid).eq('gbu_id', gbuId);
  if (error) return werfeKind(error);
  return 'geloescht';
};

/**
 * Loescht eine Gefaehrdung samt Massnahmen und Vorsorge (Kaskade) — nur im
 * Entwurf; nach der ersten Freigabe „entfallen" statt geloescht (entferneKind).
 */
export const loescheGefaehrdung = async (gbu: GbuBezug, id: string): Promise<GbuLoeschErgebnis> =>
  entferneKind(T_GEFAEHRDUNGEN, gbu, id, 'Diese Gefährdung');

// ═══ Massnahmen ═══════════════════════════════════════════════════════════

/** Umsetzung und Wirksamkeit — die einzigen Felder, die nach dem Entwurf noch geschrieben werden. */
const massnahmeStandZuZeile = (e: MassnahmeEntwurf): Zeile => ({
  umgesetzt_gemeldet_am: leerZuNull(e.umgesetztGemeldetAm),
  umgesetzt_gemeldet_von: leerZuNull(e.umgesetztGemeldetVon),
  umgesetzt_kommentar: leerZuNull(e.umgesetztKommentar),
  wirksam_geprueft_am: leerZuNull(e.wirksamGeprueftAm),
  wirksam_kuerzel: leerZuNull(e.wirksamKuerzel),
  wirksam: typeof e.wirksam === 'boolean' ? e.wirksam : null,
  wirksam_notiz: leerZuNull(e.wirksamNotiz),
});

const massnahmeZuZeile = (e: MassnahmeEntwurf): Zeile => {
  if (!e) throw new Error('Keine Maßnahme angegeben.');
  if (!MASSNAHME_ARTEN.includes(e.art)) throw new Error('Bitte die Art der Maßnahme wählen (S, T, O oder P).');
  const verweis = e.verweis ?? null;
  if (verweis !== null && !VERWEISE.includes(verweis)) throw new Error(`Unbekannter Verweis „${String(verweis)}".`);
  return {
    art: e.art,
    text: (e.text ?? '').trim(),
    // PSA gibt es nur bei P — eine PSA an einer technischen Massnahme waere eine falsche PSA-Uebersicht.
    psa_code: e.art === 'P' ? leerZuNull(e.psaCode) : null,
    verweis,
    verantwortlich: leerZuNull(e.verantwortlich),
    termin: datumOderNull(e.termin, 'Termin'),
    ...massnahmeStandZuZeile(e),
    quelle: quelleVon(e.quelle),
    sort_order: sortierung(e.sortOrder),
  };
};

/**
 * Legt eine Massnahme an einer Gefaehrdung an (ohne `id`, nur im Entwurf)
 * oder aendert sie.
 *
 * Aendern im ENTWURF schreibt alle Felder. AUSSERHALB des Entwurfs (in
 * Freigabe, freigegeben) schreibt es NUR Umsetzung und Wirksamkeit — die
 * fallen nach der Freigabe an; Art, Text, PSA, Verantwortlich und Termin
 * gehoeren zur eingefrorenen Fassung und bleiben (Riegel c). Eine archivierte
 * GBU nimmt gar nichts mehr an.
 */
export const speichereMassnahme = async (
  gbu: GbuBezug,
  gefaehrdungId: string,
  entwurf: MassnahmeEntwurf,
  id?: string,
  companyName?: string | null,
  opts?: NeueZeileOptionen,
): Promise<SifaGbuMassnahme> => {
  const { gbuId, projectId } = bezugPruefen(gbu);
  if (!entwurf) throw new Error('Keine Maßnahme angegeben.');
  const vid = (id ?? '').trim();
  if (!vid || gbu.status === 'archiviert') nurImEntwurf(gbu);
  const neueId = vid ? null : neueIdAus(opts);

  if (vid) {
    const felder = gbu.status === 'entwurf' ? massnahmeZuZeile(entwurf) : massnahmeStandZuZeile(entwurf);
    const { data, error } = await supabase
      .from(T_MASSNAHMEN)
      .update({ ...felder, updated_at: new Date().toISOString() })
      .eq('id', vid)
      .eq('gbu_id', gbuId)
      .select('*');
    if (error) return werfeKind(error);
    if (!data || data.length === 0) throw new Error('Diese Maßnahme wurde nicht gefunden (Speichern).');
    return mapMassnahme(data[0] as Zeile);
  }

  const gid = (gefaehrdungId || '').trim();
  if (!gid) throw new Error('Die Maßnahme braucht eine Gefährdung.');
  const felder = massnahmeZuZeile(entwurf);
  const { data, error } = await supabase
    .from(T_MASSNAHMEN)
    .insert({ ...(neueId ? { id: neueId } : {}), ...felder, gefaehrdung_id: gid, gbu_id: gbuId, project_id: projectId, company_name: await firmaFuer(companyName) })
    .select('*')
    .single();
  if (error) {
    if (neueId && istIdKollision(error)) return speichereMassnahme(gbu, gid, entwurf, neueId, companyName);
    return werfeKind(error);
  }
  return mapMassnahme(data as Zeile);
};

/** Loescht eine Massnahme — nur im Entwurf; nach der ersten Freigabe „entfallen" (entferneKind). */
export const loescheMassnahme = async (gbu: GbuBezug, id: string): Promise<GbuLoeschErgebnis> =>
  entferneKind(T_MASSNAHMEN, gbu, id, 'Diese Maßnahme');

// ═══ Vorsorge ═════════════════════════════════════════════════════════════

const vorsorgeZuZeile = (e: VorsorgeEntwurf): Zeile => {
  if (!e) throw new Error('Kein Vorsorgeanlass angegeben.');
  const code = (e.anlassCode ?? '').trim();
  if (!code) throw new Error('Bitte einen Vorsorgeanlass wählen.');
  if (!VORSORGE_ARTEN.includes(e.art)) throw new Error('Bitte die Art der Vorsorge wählen (Pflicht, Angebot oder Wunsch).');
  const vorschlag = e.artVorschlag ?? null;
  if (vorschlag !== null && !VORSORGE_ARTEN.includes(vorschlag)) throw new Error(`Unbekannter Vorschlag „${String(vorschlag)}".`);
  const messwert = e.messwert ?? null;
  if (messwert !== null && (typeof messwert !== 'number' || !Number.isFinite(messwert))) {
    throw new Error('Der Messwert muss eine Zahl sein (leer = keiner).');
  }
  return {
    anlass_code: code,
    messwert,
    einheit: leerZuNull(e.einheit),
    art_vorschlag: vorschlag,
    art: e.art,
    begruendung: leerZuNull(e.begruendung),
    quelle: quelleVon(e.quelle),
  };
};

/** Legt einen Vorsorgeanlass an einer Gefaehrdung an (ohne `id`) oder aendert ihn — nur im Entwurf. */
export const speichereVorsorge = async (
  gbu: GbuBezug,
  gefaehrdungId: string,
  entwurf: VorsorgeEntwurf,
  id?: string,
  companyName?: string | null,
  opts?: NeueZeileOptionen,
): Promise<SifaGbuVorsorge> => {
  const { gbuId, projectId } = bezugPruefen(gbu);
  nurImEntwurf(gbu);
  const felder = vorsorgeZuZeile(entwurf);
  const vid = (id ?? '').trim();
  const neueId = vid ? null : neueIdAus(opts);
  if (vid) {
    const { data, error } = await supabase
      .from(T_VORSORGE)
      .update({ ...felder, updated_at: new Date().toISOString() })
      .eq('id', vid)
      .eq('gbu_id', gbuId)
      .select('*');
    if (error) return werfeKind(error);
    if (!data || data.length === 0) throw new Error('Dieser Vorsorgeanlass wurde nicht gefunden (Speichern).');
    return mapVorsorge(data[0] as Zeile);
  }
  const gid = (gefaehrdungId || '').trim();
  if (!gid) throw new Error('Der Vorsorgeanlass braucht eine Gefährdung.');
  const { data, error } = await supabase
    .from(T_VORSORGE)
    .insert({ ...(neueId ? { id: neueId } : {}), ...felder, gefaehrdung_id: gid, gbu_id: gbuId, project_id: projectId, company_name: await firmaFuer(companyName) })
    .select('*')
    .single();
  if (error) {
    if (neueId && istIdKollision(error)) return speichereVorsorge(gbu, gid, entwurf, neueId, companyName);
    return werfeKind(error);
  }
  return mapVorsorge(data as Zeile);
};

/** Loescht einen Vorsorgeanlass — nur im Entwurf; nach der ersten Freigabe „entfallen" (entferneKind). */
export const loescheVorsorge = async (gbu: GbuBezug, id: string): Promise<GbuLoeschErgebnis> =>
  entferneKind(T_VORSORGE, gbu, id, 'Dieser Vorsorgeanlass');

/** Die Zeilentypen je Art — fuer den Rueckgabetyp von stelleWiederHer. */
export interface GbuKindZeilen {
  gefaehrdung: SifaGbuGefaehrdung;
  massnahme: SifaGbuMassnahme;
  vorsorge: SifaGbuVorsorge;
}

const KIND_TABELLE: Record<keyof GbuKindZeilen, { tabelle: string; map: (row: Zeile) => unknown; was: string }> = {
  gefaehrdung: { tabelle: T_GEFAEHRDUNGEN, map: (r) => mapGefaehrdung(r), was: 'Diese Gefährdung' },
  massnahme: { tabelle: T_MASSNAHMEN, map: (r) => mapMassnahme(r), was: 'Diese Maßnahme' },
  vorsorge: { tabelle: T_VORSORGE, map: (r) => mapVorsorge(r), was: 'Dieser Vorsorgeanlass' },
};

/**
 * Holt eine entfallene Zeile zurueck (`entfaellt_ab_fassung = null`) — nur
 * im Entwurf. Rueckgabe: die Zeile, frisch aus der Datenbank. Bei einer
 * Gefaehrdung kommen damit auch ihre Massnahmen und Vorsorge zurueck (die
 * tragen das Merkmal nicht selbst, siehe entferneKind).
 */
export const stelleWiederHer = async <A extends keyof GbuKindZeilen>(art: A, gbu: GbuBezug, id: string): Promise<GbuKindZeilen[A]> => {
  const ziel = KIND_TABELLE[art];
  if (!ziel) throw new Error(`Unbekannte Art „${String(art)}".`);
  const { gbuId } = bezugPruefen(gbu);
  nurImEntwurf(gbu);
  const vid = (id || '').trim();
  if (!vid) throw new Error(`${ziel.was} wurde nicht gefunden.`);
  const { data, error } = await supabase
    .from(ziel.tabelle)
    .update({ entfaellt_ab_fassung: null, updated_at: new Date().toISOString() })
    .eq('id', vid)
    .eq('gbu_id', gbuId)
    .select('*');
  if (error) return werfeKind(error);
  if (!data || data.length === 0) throw new Error(`${ziel.was} wurde nicht gefunden (Wiederherstellen).`);
  return ziel.map(data[0] as Zeile) as GbuKindZeilen[A];
};

// ═══ KI-Auswahl uebernehmen ═══════════════════════════════════════════════

/** Art einer Vorsorge, die aus dem KI-Vorschlag ohne Messwert entsteht. */
export type VorsorgeArtFuer = (anlassCode: string) => { artVorschlag: VorsorgeArt | null; art: VorsorgeArt };

/**
 * Vorgabe: der Vorschlag aus Katalogregel ohne Messwert
 * (`vorsorgeArtVorschlag(code, null)`), sonst die `standardArt` des
 * ArbMedVV-Katalogs. Unbekannter Code: 'pflicht' — der Nutzer bestaetigt die
 * Art ohnehin; im Zweifel nie die schwaechere.
 */
export const vorsorgeArtAusKatalog: VorsorgeArtFuer = (anlassCode) => {
  const artVorschlag = vorsorgeArtVorschlag(anlassCode, null) ?? null;
  const eintrag = ARBMEDVV_ANLAESSE.find((a) => a.code === anlassCode);
  return { artVorschlag, art: artVorschlag ?? eintrag?.standardArt ?? 'pflicht' };
};

const gleichText = (a: string, b: string): boolean =>
  a.replace(/\s+/g, ' ').trim().toLowerCase() === b.replace(/\s+/g, ' ').trim().toLowerCase();

/**
 * Was der KI-Kopf an der GBU ERGAENZT — rein, ohne Datenbank. Nie
 * ueberschreiben: Listen (Vorschriften, organisatorische Massnahmen) bekommen
 * nur fehlende Eintraege angehaengt, Textfelder und Notfall-Felder werden nur
 * gefuellt, wenn sie leer sind. Leeres Objekt = nichts zu tun.
 */
export const kopfErgaenzung = (gbu: SifaGbu, kopf: GbuKiKopfVorschlag | null | undefined): Partial<GbuKopfEntwurf> => {
  const aus: Partial<GbuKopfEntwurf> = {};
  if (!kopf) return aus;

  const haenge = (alt: string[], neu: string[] | undefined): string[] | null => {
    const dazu = textListe(neu).filter((n) => !alt.some((a) => gleichText(a, n)));
    return dazu.length > 0 ? [...alt, ...dazu] : null;
  };
  const vorschriften = haenge(gbu.vorschriften ?? [], kopf.vorschriften);
  if (vorschriften) aus.vorschriften = vorschriften;
  const org = haenge(gbu.orgMassnahmen ?? [], kopf.orgMassnahmen);
  if (org) aus.orgMassnahmen = org;

  const fuelle = (alt: string | null, neu: string | undefined): string | null => {
    const n = (neu ?? '').trim();
    return !(alt ?? '').trim() && n ? n : null;
  };
  const beteiligte = fuelle(gbu.beteiligte, kopf.beteiligte);
  if (beteiligte) aus.beteiligte = beteiligte;
  const unterlagen = fuelle(gbu.unterlagen, kopf.unterlagen);
  if (unterlagen) aus.unterlagen = unterlagen;
  const psa = fuelle(gbu.psaGrundausstattung, kopf.psaGrundausstattung);
  if (psa) aus.psaGrundausstattung = psa;
  const rand = fuelle(gbu.randbedingungen, kopf.randbedingungen);
  if (rand) aus.randbedingungen = rand;
  const umwelt = fuelle(gbu.umwelt, kopf.umwelt);
  if (umwelt) aus.umwelt = umwelt;
  const koordination = fuelle(gbu.koordination, kopf.koordination);
  if (koordination) aus.koordination = koordination;

  if (kopf.notfall) {
    const notfall = { ...leererNotfall(), ...(gbu.notfall ?? {}) };
    let geaendert = false;
    for (const k of NOTFALL_FELDER) {
      const neu = fuelle(notfall[k], kopf.notfall[k]);
      if (neu) {
        notfall[k] = neu;
        geaendert = true;
      }
    }
    if (geaendert) aus.notfall = notfall;
  }
  return aus;
};

/** Ergebnis von uebernehmeKiAuswahl: der (ggf. ergaenzte) Kopf und die NEU angelegten Zeilen. */
export interface KiUebernahme {
  gbu: SifaGbu;
  gefaehrdungen: SifaGbuGefaehrdung[];
  massnahmen: SifaGbuMassnahme[];
  vorsorge: SifaGbuVorsorge[];
}

type GeplanteGefaehrdung = {
  zeile: Zeile;
  massnahmen: Zeile[];
  vorsorge: Zeile[];
};

/** Baut und prueft ALLE Zeilen vor dem ersten Schreiben — ein kaputter Vorschlag schreibt nichts. */
const planeKiZeilen = (
  auswahl: GbuKiAuswahl,
  startSortierung: number,
  vorsorgeArtFuer: VorsorgeArtFuer,
): GeplanteGefaehrdung[] =>
  (auswahl?.gefaehrdungen ?? []).map((g, i) => {
    const v: GbuKiVorschlagGefaehrdung = g.vorschlag;
    const quelle: ZeilenQuelle = v.quelle === 'ersatz' ? 'ersatz' : 'ki';
    const zeile = gefaehrdungZuZeile({
      faktor: v.faktor,
      gefaehrdung: v.gefaehrdung,
      folgen: v.folgen,
      wVor: v.wVor,
      sVor: v.sVor,
      wNach: v.wNach,
      sNach: v.sNach,
      arbeitsmittelIds: v.arbeitsmittelIds ?? [],
      stoffIds: v.stoffIds ?? [],
      vorschriften: v.vorschriften ?? [],
      quelle,
      sortOrder: startSortierung + i,
    });
    if (!(zeile.gefaehrdung as string)) throw new Error('Ein Vorschlag hat keinen Gefährdungstext und wurde nicht übernommen.');

    const mGewaehlt = new Set(g.massnahmenTempIds ?? []);
    const massnahmen = (v.massnahmen ?? [])
      .filter((m) => mGewaehlt.has(m.tempId))
      .map((m, j) =>
        massnahmeZuZeile({
          art: m.art,
          text: m.text,
          psaCode: m.psaCode,
          verweis: m.verweis,
          verantwortlich: null,
          termin: null,
          umgesetztGemeldetAm: null,
          umgesetztGemeldetVon: null,
          umgesetztKommentar: null,
          wirksamGeprueftAm: null,
          wirksamKuerzel: null,
          wirksam: null,
          wirksamNotiz: null,
          quelle,
          sortOrder: j,
        }),
      );

    const vGewaehlt = new Set(g.vorsorgeTempIds ?? []);
    const vorsorge = (v.vorsorge ?? [])
      .filter((x) => vGewaehlt.has(x.tempId))
      .map((x) => {
        const code = (x.anlassCode ?? '').trim();
        const { artVorschlag, art } = vorsorgeArtFuer(code);
        return vorsorgeZuZeile({
          anlassCode: code,
          messwert: null,
          einheit: null,
          artVorschlag,
          art,
          begruendung: x.begruendung,
          quelle,
        });
      });

    return { zeile, massnahmen, vorsorge };
  });

/** Hoechste sort_order der Gefaehrdungen dieser GBU + 1 — Uebernommenes kommt ans Ende. */
const naechsteGefaehrdungsSortierung = async (gbuId: string): Promise<number> => {
  const { data, error } = await supabase
    .from(T_GEFAEHRDUNGEN)
    .select('sort_order')
    .eq('gbu_id', gbuId)
    .order('sort_order', { ascending: false })
    .limit(1)
    .maybeSingle();
  if (error) return werfe(error);
  const hoechste = Number((data as { sort_order?: unknown } | null)?.sort_order);
  return Number.isFinite(hoechste) ? hoechste + 1 : 0;
};

/**
 * Uebernimmt, was der Nutzer im Pruefbildschirm angehakt hat — NUR
 * EINFUEGEN, nie bestehende Zeilen ueberschreiben (Plan §6.2).
 *
 * Reihenfolge: (1) alle Zeilen bauen und pruefen (ein kaputter Vorschlag
 * schreibt nichts), (2) den Kopf ERGAENZEN (kopfErgaenzung, ueber
 * speichereGbuKopf mit Sperre — ein Konflikt wirft, BEVOR eine Zeile
 * entstanden ist), (3) je Gefaehrdung die Zeile, dann ihre gewaehlten
 * Massnahmen und Vorsorgeanlaesse. Gefaehrdungen kommen hinten an
 * (`sort_order` ab max + 1), Massnahmen in der Reihenfolge des Vorschlags.
 * `quelle` aus dem Vorschlag ('ki' bzw. 'ersatz').
 *
 * Vorsorge-Art: der Vorschlag hat keinen Messwert — `vorsorgeArtFuer`
 * (Vorgabe vorsorgeArtAusKatalog) liefert `artVorschlag` und `art`.
 *
 * Bricht das Netz in (3) ab, bleibt das bis dahin Angelegte stehen; die
 * Rueckgabe gibt es dann nicht — die Oberflaeche laedt neu (ladeGbu).
 */
export const uebernehmeKiAuswahl = async (
  gbu: SifaGbu,
  auswahl: GbuKiAuswahl,
  vorsorgeArtFuer: VorsorgeArtFuer = vorsorgeArtAusKatalog,
): Promise<KiUebernahme> => {
  const { gbuId, projectId } = bezugPruefen(gbu);
  nurImEntwurf(gbu);

  const start = (auswahl?.gefaehrdungen?.length ?? 0) > 0 ? await naechsteGefaehrdungsSortierung(gbuId) : 0;
  const plan = planeKiZeilen(auswahl, start, vorsorgeArtFuer);

  const ergaenzung = kopfErgaenzung(gbu, auswahl?.kopf);
  const kopf = Object.keys(ergaenzung).length > 0 ? await speichereGbuKopf(gbu, ergaenzung) : gbu;

  const ergebnis: KiUebernahme = { gbu: kopf, gefaehrdungen: [], massnahmen: [], vorsorge: [] };
  if (plan.length === 0) return ergebnis;
  const company = await eigenerFirmenname();
  const bezug = { gbu_id: gbuId, project_id: projectId, company_name: company };

  for (const p of plan) {
    const { data, error } = await supabase.from(T_GEFAEHRDUNGEN).insert({ ...p.zeile, ...bezug }).select('*').single();
    if (error) return werfeKind(error);
    const gefaehrdung = mapGefaehrdung(data as Zeile);
    ergebnis.gefaehrdungen.push(gefaehrdung);

    if (p.massnahmen.length > 0) {
      const { data: m, error: mFehler } = await supabase
        .from(T_MASSNAHMEN)
        .insert(p.massnahmen.map((z) => ({ ...z, ...bezug, gefaehrdung_id: gefaehrdung.id })))
        .select('*');
      if (mFehler) return werfeKind(mFehler);
      ergebnis.massnahmen.push(...((m ?? []) as Zeile[]).map(mapMassnahme));
    }
    if (p.vorsorge.length > 0) {
      const { data: v, error: vFehler } = await supabase
        .from(T_VORSORGE)
        .insert(p.vorsorge.map((z) => ({ ...z, ...bezug, gefaehrdung_id: gefaehrdung.id })))
        .select('*');
      if (vFehler) return werfeKind(vFehler);
      ergebnis.vorsorge.push(...((v ?? []) as Zeile[]).map(mapVorsorge));
    }
  }
  return ergebnis;
};

// ═══ Fassungen und Freigabe (RPCs Nr. 168) ═════════════════════════════════

/**
 * Friert die laufende Fassung ein (RPC `sifa_gbu_fassung_einfrieren`): legt
 * die Fassung mit `inhalt` an und setzt die GBU auf `in_freigabe` und
 * `pruefen_bis`. Die Datenbank prueft:
 *  - 'eingabe': `inhalt.gbuId` bzw. `inhalt.pruefenBis` passen nicht, das
 *    Pruefdatum liegt vor dem heutigen Berliner Tag, oder der Inhalt ist
 *    groesser als 2 MB;
 *  - 'status': nicht (mehr) im Entwurf, `inhalt.fassung` ist nicht die
 *    laufende Fassung (anderer Stand) oder die Fassung ist schon eingefroren.
 * Was ohne Datenbank pruefbar ist (Kennung, Pruefdatum), wird hier vorab
 * abgewiesen.
 */
export const fassungEinfrieren = async (
  gbuId: string,
  inhalt: GbuFassungInhalt,
  pruefenBis: string,
): Promise<{ fassungId: string; fassung: number }> => {
  const id = (gbuId || '').trim();
  if (!id) throw new Error('Keine Gefährdungsbeurteilung gewählt.');
  if (!inhalt || typeof inhalt !== 'object') throw new Error('Es liegt kein Inhalt zum Einfrieren vor.');
  if (inhalt.gbuId !== id) throw new Error('Der Inhalt gehört zu einer anderen Gefährdungsbeurteilung.');
  const bis = pflichtDatum(pruefenBis, 'Prüfen bis');
  if (inhalt.pruefenBis != null && inhalt.pruefenBis !== bis) {
    throw new Error('Das Prüfdatum im Inhalt weicht vom gewählten Prüfdatum ab — bitte die Fassung neu aufbauen.');
  }

  const row = await rufeRpc('sifa_gbu_fassung_einfrieren', { p_gbu_id: id, p_inhalt: inhalt, p_pruefen_bis: bis }, {
    ...RPC_TEXTE_GEMEINSAM,
    status: 'Diese Gefährdungsbeurteilung ist nicht mehr im Entwurf, oder ihre Fassung wurde inzwischen eingefroren bzw. passt nicht zum aktuellen Stand — bitte neu laden.',
    eingabe: 'Der Inhalt passt nicht zu dieser Gefährdungsbeurteilung (Kennung oder Prüfdatum), das Prüfdatum liegt in der Vergangenheit, oder der Inhalt ist zu groß.',
  });
  const fassungId = str(pick(row, 'fassung_id', 'fassungId')).trim();
  if (!fassungId) throw new Error('Die Datenbank hat keine Fassung zurückgegeben — bitte neu laden und prüfen.');
  return { fassungId, fassung: num(pick(row, 'fassung'), num(inhalt.fassung, 0)) };
};

/**
 * Zieht eine noch NICHT freigegebene Fassung zurueck (RPC
 * `sifa_gbu_freigabe_zurueckziehen`): Links und Fassung weg, GBU zurueck auf
 * `entwurf`. Rueckgabe wie die RPC: GBU, Fassungsnummer, Zahl der
 * geloeschten Links.
 */
export const freigabeZurueckziehen = async (fassungId: string): Promise<{ gbuId: string; fassung: number; links: number }> => {
  const id = (fassungId || '').trim();
  if (!id) throw new Error('Keine Fassung gewählt.');
  const row = await rufeRpc('sifa_gbu_freigabe_zurueckziehen', { p_fassung_id: id }, {
    ...RPC_TEXTE_GEMEINSAM,
    status: 'Diese Fassung ist bereits freigegeben und lässt sich nicht mehr zurückziehen.',
  });
  return { gbuId: str(pick(row, 'gbu_id', 'gbuId')), fassung: num(pick(row, 'fassung'), 0), links: num(pick(row, 'links'), 0) };
};

const ROLLE_TEXT: Record<UnterschriftRolle, string> = { erstellt: 'Erstellt', geprueft: 'Geprüft', freigegeben: 'Freigegeben' };

/**
 * Prueft Unterschriften mit denselben Grenzen wie die RPCs (Nr. 168).
 * BILDPFLICHT: eine Unterschrift am Geraet oder per Link (`weg` 'geraet' |
 * 'link') braucht ein Bild (`data:image/…`, nicht leer, hoechstens 150 000
 * Zeichen) — ohne Bild waere sie nur ein Name. Nur `weg 'papier'` darf ohne
 * Bild (der Scan traegt die Unterschrift). `bildPflichtRolle`: diese Rolle
 * braucht ein Bild, gleich welcher Weg (digitale Freigabe: „Freigegeben").
 */
const pruefeUnterschriften = (
  liste: GbuUnterschrift[] | null | undefined,
  opt: { bildPflichtRolle?: UnterschriftRolle } = {},
): GbuUnterschrift[] => {
  if ((liste ?? []).length > MAX_UNTERSCHRIFTEN) throw new Error(`Höchstens ${MAX_UNTERSCHRIFTEN} Unterschriften auf einmal.`);
  return (liste ?? []).map((u) => {
    if (!u || !ROLLEN.includes(u.rolle)) throw new Error('Unbekannte Rolle an einer Unterschrift.');
    const name = (u.name ?? '').trim();
    if (!name || name.length > 120) throw new Error('Jede Unterschrift braucht einen Namen (höchstens 120 Zeichen).');
    if ((u.funktion ?? '').trim().length > 120) throw new Error('Die Funktion an einer Unterschrift ist zu lang (höchstens 120 Zeichen).');
    if (!['geraet', 'link', 'papier'].includes(u.weg)) throw new Error('Unbekannter Weg einer Unterschrift.');
    // Eine leere Zeichenkette ist kein Bild, sondern keins.
    const bild = typeof u.bild === 'string' && u.bild.trim() ? u.bild : null;
    if (bild !== null && (!bild.startsWith('data:image/') || bild.length > UNTERSCHRIFT_MAX_ZEICHEN)) {
      throw new Error('Eine Unterschrift ist unlesbar oder zu groß.');
    }
    if (bild === null && (u.weg !== 'papier' || opt.bildPflichtRolle === u.rolle)) {
      throw new Error(`Die Unterschrift „${ROLLE_TEXT[u.rolle]}" von ${name} hat kein Bild — bitte am Gerät unterschreiben.`);
    }
    return { rolle: u.rolle, name, funktion: leerZuNull(u.funktion), datum: str(u.datum), bild, weg: u.weg };
  });
};

/** Rollen, die VOR der Freigabe unterschreiben; „Freigegeben" geht ueber freigeben bzw. den Link. */
const VORAB_ROLLEN: readonly UnterschriftRolle[] = ['erstellt', 'geprueft'];

/**
 * Unterschriften „Erstellt" und „Geprueft" am Geraet an eine eingefrorene,
 * noch nicht freigegebene Fassung haengen (RPC
 * `sifa_gbu_fassung_unterschreiben`). Eine Rolle, die schon unterschrieben
 * hat, wird ERSETZT (neu unterschrieben), nicht verdoppelt. Rueckgabe: alle
 * Unterschriften der Fassung danach, geprueft gemappt (mapUnterschriften).
 *
 * Vorab ohne Datenbank: mindestens eine Unterschrift, nur diese zwei Rollen,
 * jede hoechstens einmal, Name/Funktion/Bild mit den Grenzen der RPC; am
 * Geraet bzw. per Link nie ohne Bild (pruefeUnterschriften).
 * `{ ok: false }` → GbuRpcFehler: 'status' (schon freigegeben bzw. GBU nicht
 * mehr `in_freigabe` an dieser Fassung), 'eingabe' (Unterschrift ungueltig),
 * 'zugriff'.
 */
export const unterschreibeFassung = async (fassungId: string, unterschriften: GbuUnterschrift[]): Promise<GbuUnterschrift[]> => {
  const id = (fassungId || '').trim();
  if (!id) throw new Error('Keine Fassung gewählt.');
  const liste = pruefeUnterschriften(unterschriften);
  if (liste.length === 0) throw new Error('Bitte zuerst unterschreiben.');
  if (liste.some((u) => !VORAB_ROLLEN.includes(u.rolle))) {
    throw new Error('Hier unterschreiben nur „Erstellt" und „Geprüft" — „Freigegeben" geht über die Freigabe.');
  }
  if (new Set(liste.map((u) => u.rolle)).size !== liste.length) throw new Error('Jede Rolle unterschreibt höchstens einmal.');

  const row = await rufeRpc('sifa_gbu_fassung_unterschreiben', { p_fassung_id: id, p_unterschriften: liste }, {
    ...RPC_TEXTE_GEMEINSAM,
    status: 'Diese Fassung ist schon freigegeben oder steht nicht mehr zur Freigabe an — bitte neu laden.',
    eingabe: 'Die Unterschrift wurde abgelehnt — Rolle, Name (höchstens 120 Zeichen) oder Bild sind ungültig.',
  });
  return mapUnterschriften(row.unterschriften);
};

/**
 * Gibt eine eingefrorene Fassung frei (RPC `sifa_gbu_freigeben`): digital
 * mit den Unterschriften vom Geraet, oder auf Papier mit dem abgelegten Scan
 * (`legeScanAb`). Die Datenbank haengt die Unterschriften an, setzt GBU und
 * Fassung auf freigegeben, erledigt offene GBU-Fristen und legt die
 * Ueberpruefungsfrist an. Digital braucht es die Unterschrift „Freigegeben"
 * (schon an der Fassung oder jetzt mitgegeben), sonst 'eingabe'. Schon
 * freigegeben = `{ bereits: true }`, kein Fehler (zweiter Klick).
 * Vorab: Unterschriften am Geraet/per Link nur mit Bild; digital braucht die
 * mitgegebene Unterschrift „Freigegeben" immer ein Bild. Papier darf ohne.
 * `fristId`/`fristenErledigt` wie die Kern-Antwort (null/0 bei `bereits`).
 */
export const freigeben = async (
  fassungId: string,
  opt: { weg: FreigabeWeg; unterschriften: GbuUnterschrift[]; scanDokumentId?: string | null },
): Promise<{ bereits: boolean; fristId: string | null; fristenErledigt: number }> => {
  const id = (fassungId || '').trim();
  if (!id) throw new Error('Keine Fassung gewählt.');
  if (opt?.weg !== 'digital' && opt?.weg !== 'papier') throw new Error('Bitte den Weg der Freigabe wählen (digital oder Papier).');
  const scan = leerZuNull(opt.scanDokumentId);
  if (opt.weg === 'papier' && !scan) throw new Error('Für die Freigabe auf Papier bitte zuerst den unterschriebenen Scan hochladen.');
  // Digital: die Unterschrift „Freigegeben" (falls mitgegeben) braucht ein Bild, auch mit weg 'papier'.
  const unterschriften = pruefeUnterschriften(opt.unterschriften, opt.weg === 'digital' ? { bildPflichtRolle: 'freigegeben' } : {});

  const row = await rufeRpc('sifa_gbu_freigeben', {
    p_fassung_id: id,
    p_weg: opt.weg,
    p_unterschriften: unterschriften,
    p_scan_dokument_id: scan,
  }, {
    ...RPC_TEXTE_GEMEINSAM,
    status: 'Diese Fassung steht nicht (mehr) zur Freigabe an — bitte neu laden.',
    eingabe: 'Die Freigabe wurde abgelehnt: digital fehlt die Unterschrift „Freigegeben", oder Unterschriften bzw. Scan (aus diesem Projekt) sind ungültig.',
  });
  return {
    bereits: row.bereits === true,
    fristId: strOrNull(pick(row, 'frist_id', 'fristId')),
    fristenErledigt: num(pick(row, 'fristen_erledigt', 'fristenErledigt'), 0),
  };
};

/**
 * Legt einen Freigabe-Link fuer EINE Fassung in Freigabe an (RPC
 * `sifa_gbu_freigabe_link_anlegen`): Rolle, vorbelegter Name (hoechstens 120
 * Zeichen), Gueltigkeit 1–14 Tage (Vorgabe 14, Grenze der RPC). Die
 * Link-URL `#/gbu-freigabe/<token>` baut die Oberflaeche.
 */
export const legeFreigabeLinkAn = async (
  fassungId: string,
  opt: { rolle: UnterschriftRolle; name?: string | null; tage?: number },
): Promise<{ token: string; gueltigBis: string; linkId: string }> => {
  const id = (fassungId || '').trim();
  if (!id) throw new Error('Keine Fassung gewählt.');
  if (!ROLLEN.includes(opt?.rolle)) throw new Error('Bitte die Rolle für den Link wählen.');
  const tage = opt.tage ?? FREIGABE_LINK_MAX_TAGE;
  if (!Number.isInteger(tage) || tage < 1 || tage > FREIGABE_LINK_MAX_TAGE) {
    throw new Error(`Der Link gilt 1 bis ${FREIGABE_LINK_MAX_TAGE} Tage.`);
  }
  const name = leerZuNull(opt.name);
  if (name && name.length > 120) throw new Error('Der vorbelegte Name ist zu lang (höchstens 120 Zeichen).');

  const row = await rufeRpc('sifa_gbu_freigabe_link_anlegen', {
    p_fassung_id: id,
    p_rolle: opt.rolle,
    p_name: name,
    p_tage: tage,
  }, {
    ...RPC_TEXTE_GEMEINSAM,
    status: 'Ein Freigabe-Link geht nur für die Fassung, die gerade in Freigabe ist — sie ist schon freigegeben oder zurückgezogen. Bitte neu laden.',
    eingabe: `Rolle, Name (höchstens 120 Zeichen) oder Laufzeit (1–${FREIGABE_LINK_MAX_TAGE} Tage) wurden abgelehnt.`,
  });
  const token = str(pick(row, 'token')).trim();
  if (!token) throw new Error('Die Datenbank hat keinen Link geliefert — bitte erneut versuchen.');
  return { token, gueltigBis: str(pick(row, 'gueltig_bis', 'gueltigBis')), linkId: str(pick(row, 'link_id', 'linkId')) };
};

// ═══ Ablage (Projektdokumente) ════════════════════════════════════════════

/** Wer ablegt — wie angemeldeterBenutzer(); fehlt er, wird er nachgeschlagen. */
export type GbuBenutzer = Partial<AngemeldeterBenutzer> | null | undefined;

/** Kennung der Fassung in der Ablage (`fassung_ref`): `GBU-<nr>-F<fassung>`. */
export const gbuFassungRef = (nummer: number, fassung: number): string => `GBU-${nummer}-F${fassung}`;

/** Titel fuer Dateinamen: nur Buchstaben, Ziffern, Bindestrich; hoechstens 60 Zeichen. */
const dateiTeil = (titel: string): string =>
  (titel || '')
    .normalize('NFC')
    .replace(/[^\p{L}\p{N}]+/gu, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60);

const ablageKlartext = (e: unknown): string => {
  if (istFehlendeKennzeichnung(e)) {
    return 'Die Ablage kennt die Kennzeichnung erzeugter Unterlagen noch nicht (Migration Nr. 160/161 fehlt).';
  }
  if (e instanceof AblageFehler && e.code === '23514') {
    // Absicherung: der CHECK kennt 'gbu' erst ab Nr. 167.
    return 'Die Ablage kennt die Kennung „gbu" für erzeugte Unterlagen noch nicht (Migration Nr. 167 fehlt). Die Datei wurde nicht abgelegt — bitte an die Verwaltung wenden.';
  }
  return describeError(e);
};

/**
 * Legt eine Datei ueber uploadDokument ab — Doppelablage-sicher: liegt die
 * Kennung schon, wird nichts hochgeladen; gewinnt ein zweiter Browser den
 * Wettlauf (23505), gilt dessen Zeile. Liefert die Dokument-Id.
 */
const legeErzeugtAb = async (
  projectId: string,
  datei: Blob,
  dateiname: string,
  entwurf: DokumentEntwurf,
  benutzer: GbuBenutzer,
): Promise<string> => {
  const ref = (entwurf.fassung_ref || '').trim();
  try {
    const vorhanden = ref ? await findeErzeugteUnterlage(projectId, GBU_ERZEUGT_AUS, ref) : null;
    if (vorhanden?.id) return vorhanden.id;
    const wer = benutzer?.id || benutzer?.name ? benutzer : await angemeldeterBenutzer();
    const dokument = await uploadDokument({
      projectId,
      datei,
      dateiname,
      entwurf,
      benutzerId: wer?.id ?? null,
      benutzerName: wer?.name ?? null,
    });
    return dokument.id;
  } catch (e) {
    const stand = e instanceof AblageFehler ? e.rueckbau : null;
    if (stand?.verwaist && !(await entferneDatei(stand.pfad))) {
      console.warn('Verwaiste Datei in der Ablage (GBU):', stand.pfad);
    }
    if (istDoppelablage(e) && ref) {
      const gewinner = await findeErzeugteUnterlage(projectId, GBU_ERZEUGT_AUS, ref).catch(() => null);
      if (gewinner?.id) return gewinner.id;
      throw new Error('Diese Fassung liegt bereits in der Ablage, ließ sich aber gerade nicht wiederfinden. Bitte neu laden.');
    }
    throw new Error(ablageKlartext(e));
  }
};

/**
 * Legt das PDF einer FREIGEGEBENEN Fassung in den Projektdokumenten ab
 * (Kategorie `gefaehrdungen`, Ordner GBU_ABLAGE_ORDNER, `erzeugt_aus 'gbu'`,
 * `fassung_ref` `GBU-<nr>-F<fassung>`) und setzt danach `pdf_dokument_id`
 * an der Fassung — nur `where pdf_dokument_id is null` (der Trigger laesst
 * nach der Freigabe nichts anderes zu). Liefert die Dokument-Id.
 *
 * Erst nach der Freigabe: ein zurueckgezogener Entwurf traegt dieselbe
 * Fassungsnummer wie sein Nachfolger, und eine vorab abgelegte Datei
 * blockierte die Kennung fuer das unterschriebene Blatt.
 *
 * Idempotent: traegt die Fassung schon ein PDF, kommt dessen Id zurueck;
 * liegt die Kennung schon in der Ablage (Doppelablage, zweiter Browser),
 * wird das vorhandene Dokument eingetragen statt ein zweites hochzuladen.
 */
export const legeFassungsPdfAb = async (
  projectId: string,
  fassung: SifaGbuFassung,
  blob: Blob,
  benutzer?: GbuBenutzer,
): Promise<string> => {
  const pid = (projectId || '').trim();
  if (!pid || pid === 'temp-id') throw new Error('Das Projekt muss erst gespeichert werden, bevor das PDF abgelegt werden kann.');
  const fid = (fassung?.id || '').trim();
  if (!fid) throw new Error('Keine Fassung gewählt.');
  if (fassung.pdfDokumentId) return fassung.pdfDokumentId;
  if (!fassung.freigegebenAm) throw new Error('Das PDF wird erst nach der Freigabe abgelegt.');
  if (!blob || !(blob.size > 0)) throw new Error('Es liegt kein PDF zum Ablegen vor.');

  const nummer = num(fassung.inhalt?.nummer, 0);
  const titel = str(fassung.inhalt?.titel).trim();
  const dateiname = `GBU-${nummer}_Fassung-${fassung.fassung}${dateiTeil(titel) ? `_${dateiTeil(titel)}` : ''}.pdf`;
  const dokumentId = await legeErzeugtAb(pid, blob, dateiname, {
    kategorie: 'gefaehrdungen',
    ordner: GBU_ABLAGE_ORDNER,
    titel: `GBU Nr. ${nummer}${titel ? ` – ${titel}` : ''} (Fassung ${fassung.fassung})`,
    beschreibung: 'Gefährdungsbeurteilung nach § 5 ArbSchG — freigegebene Fassung.',
    tags: ['GBU', 'freigegeben'],
    module: [],
    stand: `Fassung ${fassung.fassung}`,
    nurAdmin: false,
    erzeugt_aus: GBU_ERZEUGT_AUS,
    fassung_ref: gbuFassungRef(nummer, fassung.fassung),
  }, benutzer);

  const { data, error } = await supabase
    .from(T_FASSUNGEN)
    .update({ pdf_dokument_id: dokumentId })
    .eq('id', fid)
    .is('pdf_dokument_id', null)
    .select('id, pdf_dokument_id');
  if (error) return werfe(error);
  if (data && data.length > 0) return str((data[0] as Zeile).pdf_dokument_id) || dokumentId;

  // 0 Zeilen: ein zweiter Browser hat schon eingetragen — dessen Stand gilt.
  const { data: jetzt, error: leseFehler } = await supabase.from(T_FASSUNGEN).select('id, pdf_dokument_id').eq('id', fid).maybeSingle();
  if (leseFehler) return werfe(leseFehler);
  const eingetragen = strOrNull((jetzt as Zeile | null)?.pdf_dokument_id);
  if (eingetragen) return eingetragen;
  throw new Error('Das PDF liegt in der Ablage, ließ sich aber nicht an die Fassung hängen — fehlt die Berechtigung?');
};

/**
 * Legt den unterschriebenen SCAN einer Fassung (Papierweg) in den
 * Projektdokumenten ab und liefert die Dokument-Id fuer `freigeben({ weg:
 * 'papier', scanDokumentId })`. Kategorie und Kennzeichnung wie das PDF —
 * der Scan ist der Freigabenachweis und nur von der Verwaltung loeschbar.
 *
 * Jeder Upload bekommt eine EIGENE Kennung (`GBU-<nr>-F<fassung>-Scan-<x>`):
 * ein schlecht lesbarer Scan laesst sich vor der Freigabe durch einen zweiten
 * ersetzen; welcher gilt, sagt `scan_dokument_id` an der Fassung.
 */
export const legeScanAb = async (
  projectId: string,
  gbu: Pick<SifaGbu, 'nummer' | 'titel'>,
  fassung: Pick<SifaGbuFassung, 'id' | 'fassung'>,
  datei: File | Blob,
  benutzer?: GbuBenutzer,
): Promise<string> => {
  const pid = (projectId || '').trim();
  if (!pid || pid === 'temp-id') throw new Error('Das Projekt muss erst gespeichert werden, bevor der Scan abgelegt werden kann.');
  if (!(fassung?.id || '').trim()) throw new Error('Keine Fassung gewählt.');
  if (!datei || !(datei.size > 0)) throw new Error('Die Datei ist leer.');

  const nummer = num(gbu?.nummer, 0);
  const originalname = ((datei as File).name || '').trim();
  const endung = /\.([a-zA-Z0-9]{1,8})$/.exec(originalname)?.[1]?.toLowerCase() || 'pdf';
  const dateiname = `GBU-${nummer}_Fassung-${fassung.fassung}_Scan.${endung}`;
  const absage = pruefeDatei({ name: dateiname, size: datei.size, type: datei.type });
  if (absage) throw new Error(absage);

  const titel = (gbu?.titel ?? '').trim();
  return legeErzeugtAb(pid, datei, dateiname, {
    kategorie: 'gefaehrdungen',
    ordner: GBU_ABLAGE_ORDNER,
    titel: `GBU Nr. ${nummer}${titel ? ` – ${titel}` : ''} (Fassung ${fassung.fassung}) – unterschriebener Scan`,
    beschreibung: 'Unterschriebene Freigabe auf Papier (Scan).',
    tags: ['GBU', 'Scan'],
    module: [],
    stand: `Fassung ${fassung.fassung}`,
    nurAdmin: false,
    erzeugt_aus: GBU_ERZEUGT_AUS,
    fassung_ref: `${gbuFassungRef(nummer, fassung.fassung)}-Scan-${crypto.randomUUID().slice(0, 8)}`,
  }, benutzer);
};

// ═══ Lebenslauf ═══════════════════════════════════════════════════════════

/**
 * Oeffnet die naechste Fassung als ENTWURF: nur aus `freigegeben`,
 * `fassung + 1`, Revision + 1 — bedingt auf Status UND Revision (Riegel b).
 * Die freigegebene Fassung bleibt gueltig, bis die neue freigegeben ist
 * (`freigegebene_fassung`, `pruefen_bis` bleiben stehen). Hat ein zweiter
 * Browser schon fortgeschrieben (Entwurf mit fassung + 1), ist dessen Stand
 * das Ergebnis — kein Fehler.
 */
export const fortschreiben = async (gbu: SifaGbu): Promise<SifaGbu> => {
  const id = (gbu?.id || '').trim();
  if (!id) throw new Error('Keine Gefährdungsbeurteilung gewählt.');
  if (gbu.status !== 'freigegeben') throw new Error('Fortschreiben geht nur bei einer freigegebenen Gefährdungsbeurteilung.');

  const { data, error } = await supabase
    .from(T_GBU)
    .update({ status: 'entwurf', fassung: gbu.fassung + 1, revision: gbu.revision + 1, updated_at: new Date().toISOString() })
    .eq('id', id)
    .eq('status', 'freigegeben')
    .eq('revision', gbu.revision)
    .select('*');
  if (error) return werfe(error);
  if (data && data.length > 0) return mapGbu(data[0] as Zeile);

  const vorhanden = await ladeKopf(id);
  if (!vorhanden) throw new Error('Diese Gefährdungsbeurteilung wurde nicht gefunden (Fortschreiben).');
  if (vorhanden.status === 'entwurf' && vorhanden.fassung === gbu.fassung + 1) return vorhanden;
  if (vorhanden.status !== 'freigegeben' || vorhanden.revision !== gbu.revision) throw new GbuKonfliktError();
  throw new Error(`Die Gefährdungsbeurteilung konnte nicht fortgeschrieben werden. ${MELDUNG_KEIN_RECHT}`);
};

/**
 * Schliesst die offenen Ueberpruefungsfristen einer GBU (`sifa_fristen`,
 * `kategorie 'gbu'`, `gbu_id`, nur `erledigt_am is null`) mit dem heutigen
 * Berliner Tag. Eine archivierte GBU wird nicht mehr ueberprueft — ihre Frist
 * stuende sonst rot in der Liste. WIRFT NIE: das Archivieren ist da schon
 * geschehen; ein Fehler wird gemeldet (console.warn), nicht zurueckgedreht.
 * Fehlt `gbu_id` oder die Tabelle (Nr. 92/167 offen), gibt es keine solche Frist.
 */
const schliesseGbuFristen = async (gbuId: string): Promise<void> => {
  try {
    const { error } = await supabase
      .from(T_FRISTEN)
      .update({ erledigt_am: heuteBerlin(), updated_at: new Date().toISOString() })
      .eq('gbu_id', gbuId)
      .eq('kategorie', 'gbu')
      .is('erledigt_am', null);
    if (error) throw error;
  } catch (e) {
    if (istFehlendesSchema(e) || istFehlendeSpalte(e)) return;
    console.warn('GBU archiviert, aber die offenen Überprüfungsfristen wurden nicht geschlossen:', describeError(e));
  }
};

/**
 * Archiviert eine GBU aus `entwurf` oder `freigegeben` (bedingt auf Revision).
 * Aus `in_freigabe` nicht: offene Freigabe-Links gaeben sie sonst spaeter
 * wieder frei — zuerst zurueckziehen. Schon archiviert = Ergebnis, kein Fehler.
 * Danach (auch bei „schon archiviert") werden ihre offenen GBU-Fristen
 * geschlossen (schliesseGbuFristen; ein Fehler dort aendert das Ergebnis nicht).
 */
export const archiviereGbu = async (gbu: SifaGbu): Promise<SifaGbu> => {
  const id = (gbu?.id || '').trim();
  if (!id) throw new Error('Keine Gefährdungsbeurteilung gewählt.');
  if (gbu.status === 'in_freigabe') throw new Error('Diese Gefährdungsbeurteilung ist in Freigabe — bitte zuerst die Freigabe zurückziehen.');
  if (gbu.status === 'archiviert') {
    await schliesseGbuFristen(id);
    return gbu;
  }

  const { data, error } = await supabase
    .from(T_GBU)
    .update({ status: 'archiviert', revision: gbu.revision + 1, updated_at: new Date().toISOString() })
    .eq('id', id)
    .eq('revision', gbu.revision)
    .in('status', ['entwurf', 'freigegeben'])
    .select('*');
  if (error) return werfe(error);
  if (data && data.length > 0) {
    await schliesseGbuFristen(id);
    return mapGbu(data[0] as Zeile);
  }

  const vorhanden = await ladeKopf(id);
  if (!vorhanden) throw new Error('Diese Gefährdungsbeurteilung wurde nicht gefunden (Archivieren).');
  if (vorhanden.status === 'archiviert') {
    await schliesseGbuFristen(id);
    return vorhanden;
  }
  if (vorhanden.status === 'in_freigabe') throw new Error('Diese Gefährdungsbeurteilung ist inzwischen in Freigabe — bitte zuerst die Freigabe zurückziehen.');
  if (vorhanden.revision !== gbu.revision) throw new GbuKonfliktError();
  throw new Error(`Die Gefährdungsbeurteilung konnte nicht archiviert werden. ${MELDUNG_KEIN_RECHT}`);
};

const MELDUNG_HAT_FASSUNG =
  'Diese Gefährdungsbeurteilung hat schon eine eingefrorene Fassung und lässt sich nicht löschen — bitte archivieren. Fassungen sind Nachweise und bleiben erhalten.';

/** Fassungen einer GBU (head-Count). */
const zaehleFassungen = async (gbuId: string): Promise<number> => {
  const { count, error } = await supabase
    .from(T_FASSUNGEN)
    .select('id', { count: 'exact', head: true })
    .eq('gbu_id', gbuId);
  if (error) return werfe(error);
  return typeof count === 'number' ? count : 0;
};

/**
 * Loescht einen ENTWURF ohne jede Fassung — samt Gefaehrdungen, Massnahmen
 * und Vorsorge (Kaskade). Erst zaehlen (Klartext), der harte Riegel ist der
 * Fremdschluessel `sifa_gbu_fassungen.gbu_id … restrict` (23001). Die
 * Bedingung `status = 'entwurf'` sitzt im DELETE selbst. true = geloescht,
 * false = gibt es nicht (mehr); sonst Error mit Klartext.
 */
export const loescheGbu = async (gbu: Pick<SifaGbu, 'id' | 'status'>): Promise<boolean> => {
  const id = (gbu?.id || '').trim();
  if (!id) return false;
  if (gbu.status !== 'entwurf') throw new Error('Nur Entwürfe lassen sich löschen — diese Gefährdungsbeurteilung bitte archivieren.');
  if ((await zaehleFassungen(id)) > 0) throw new Error(MELDUNG_HAT_FASSUNG);

  const { data, error } = await supabase.from(T_GBU).delete().eq('id', id).eq('status', 'entwurf').select('id');
  if (error) {
    if (istFremdschluesselSperre(error)) throw new Error(MELDUNG_HAT_FASSUNG);
    return werfe(error);
  }
  if (data && data.length > 0) return true;

  const vorhanden = await ladeKopf(id);
  if (!vorhanden) return false;
  if (vorhanden.status !== 'entwurf') throw new Error('Diese Gefährdungsbeurteilung ist inzwischen kein Entwurf mehr und lässt sich nicht löschen — bitte archivieren.');
  throw new Error(`Die Gefährdungsbeurteilung konnte nicht gelöscht werden. ${MELDUNG_KEIN_RECHT}`);
};

// ═══ Zeit buchen (Plan §10) ═══════════════════════════════════════════════

/** Anzeigename des angemeldeten Nutzers (fuer `durchgefuehrt_von`) — best effort. */
const eigenerName = async (): Promise<string | null> => (await angemeldeterBenutzer()).name ?? null;

/** Eine an der GBU erfasste Zeit (Zeile in `sifa_taetigkeiten` mit `gbu_id`). */
export interface GbuZeit {
  id: string;
  /** ISO-Datum yyyy-mm-dd */
  datum: string;
  dauerMinuten: number;
  titel: string;
  beschreibung: string | null;
  durchgefuehrtVon: string | null;
  /** Riegel: gesetzt = auf eine LV-Position gebucht (markiereTaetigkeitGebucht). */
  gebuchtAm: string | null;
  gebuchtePositionId: string | null;
  gebuchteMenge: number | null;
}

const ZEIT_SPALTEN = 'id, datum, dauer_minuten, titel, beschreibung, durchgefuehrt_von, gebucht_am, gebuchte_position_id, gebuchte_menge';

export const mapGbuZeit = (row: Zeile): GbuZeit => ({
  id: str(row.id),
  datum: iso(row.datum),
  dauerMinuten: num(row.dauer_minuten, 0),
  titel: str(row.titel),
  beschreibung: strOrNull(row.beschreibung),
  durchgefuehrtVon: strOrNull(row.durchgefuehrt_von),
  gebuchtAm: strOrNull(row.gebucht_am),
  gebuchtePositionId: strOrNull(row.gebuchte_position_id),
  gebuchteMenge: numOrNull(row.gebuchte_menge),
});

/**
 * Die an einer GBU erfassten Zeiten, neueste zuerst (Datum, dann Anlage).
 * Fail-open: fehlt die Tabelle (Nr. 92) oder die Spalte `gbu_id` bzw.
 * `gebuchte_menge` (Nr. 167), kommt eine leere Liste.
 */
export const ladeGbuZeiten = async (gbuId: string): Promise<GbuZeit[]> => {
  const id = (gbuId || '').trim();
  if (!id) return [];
  try {
    const { data, error } = await supabase
      .from(T_TAETIGKEITEN)
      .select(ZEIT_SPALTEN)
      .eq('gbu_id', id)
      .order('datum', { ascending: false })
      .order('created_at', { ascending: false });
    if (error) throw error;
    return ((data ?? []) as Zeile[]).map(mapGbuZeit);
  } catch (e) {
    if (istFehlendesSchema(e) || istFehlendeSpalte(e)) {
      console.warn('GBU-Zeiten nicht lesbar — Migration Nr. 92/167 noch nicht gefahren.');
      return [];
    }
    return werfe(e);
  }
};

/** Titel der Taetigkeit im Nachweis: „GBU Nr. n – Titel". */
export const gbuZeitTitel = (gbu: Pick<SifaGbu, 'nummer' | 'titel'>): string => {
  const titel = (gbu?.titel ?? '').trim();
  return `GBU Nr. ${num(gbu?.nummer, 0)}${titel ? ` – ${titel}` : ''}`;
};

/**
 * Erfasst die Zeit an einer GBU als Zeile im Taetigkeitsnachweis
 * (`sifa_taetigkeiten`, `art 'gbu'`, `gbu_id`, Titel „GBU Nr. n – Titel")
 * und liefert ihre Id — DIESE Id geht danach an markiereTaetigkeitGebucht.
 * Datum als ISO-Zeichenkette (nie `new Date`), Dauer 1–1440 Minuten
 * (gerundet). Ohne `durchgefuehrtVon` der angemeldete Nutzer.
 */
export const erfasseGbuZeit = async (
  projectId: string,
  gbu: Pick<SifaGbu, 'id' | 'nummer' | 'titel'>,
  e: { datum: string; dauerMinuten: number; beschreibung?: string | null; durchgefuehrtVon?: string | null; companyName?: string | null },
): Promise<string> => {
  const pid = (projectId || '').trim();
  if (!pid) throw new Error('Ohne Projekt lässt sich keine Zeit erfassen.');
  const gid = (gbu?.id || '').trim();
  if (!gid) throw new Error('Keine Gefährdungsbeurteilung gewählt.');
  const datum = pflichtDatum(e?.datum, 'Datum');
  const dauer = typeof e.dauerMinuten === 'number' ? Math.round(e.dauerMinuten) : Number.NaN;
  if (!Number.isFinite(dauer) || dauer < 1 || dauer > DAUER_MAX_MINUTEN) {
    throw new Error(`Die Dauer muss zwischen 1 Minute und ${DAUER_MAX_MINUTEN / 60} Stunden liegen.`);
  }

  const [company, wer] = await Promise.all([
    firmaFuer(e.companyName),
    leerZuNull(e.durchgefuehrtVon) ? Promise.resolve(null) : eigenerName(),
  ]);
  const { data, error } = await supabase
    .from(T_TAETIGKEITEN)
    .insert({
      project_id: pid,
      datum,
      dauer_minuten: dauer,
      art: 'gbu',
      titel: gbuZeitTitel(gbu),
      beschreibung: leerZuNull(e.beschreibung),
      durchgefuehrt_von: leerZuNull(e.durchgefuehrtVon) ?? wer,
      bezug: `GBU Nr. ${num(gbu.nummer, 0)}`,
      gbu_id: gid,
      company_name: company,
    })
    .select('id')
    .single();
  if (error) return werfe(error);
  const id = str((data as Zeile | null)?.id).trim();
  if (!id) throw new Error('Die Tätigkeit wurde nicht bestätigt — bitte im Tätigkeitsnachweis prüfen.');
  return id;
};

// ═══ Gutschrift auf dem Server pruefen (rein lesend) ══════════════════════

/** Steht die Gutschrift einer GBU-Zeit schon auf dem Server? */
export type GutschriftStand = 'vorhanden' | 'fehlt' | 'unbekannt';

/**
 * Form der Log-Id einer GBU-Zeit: `gbu-zeit-<taetigkeitId>`
 * (gbuBooking.gbuZeitLogId). Nur Buchstaben, Ziffern, Bindestrich — der Wert
 * geht in einen PostgREST-Filter.
 */
const GBU_ZEIT_LOG_ID = /^gbu-zeit-[A-Za-z0-9-]{1,64}$/;

type QuellenBefund = 'ja' | 'nein' | 'unklar';

/**
 * Buchungsjournal: `gbu-zeit-…` ist keine uuid, also traegt die gespiegelte
 * Zeile einen abgeleiteten Schluessel und die JSON-Id steht in
 * `legacy_entry_id` (serviceJournal.journalIdDecision). Auch eine
 * zurueckgenommene Zeile (`voided_at`) zaehlt: die Gutschrift gab es, eine
 * Ruecknahme war Absicht — nachgeholt wird sie nicht.
 */
const gutschriftImJournal = async (projectId: string, logId: string): Promise<QuellenBefund> => {
  try {
    const { data, error } = await supabase
      .from('service_journal')
      .select('id, voided_at')
      .eq('project_id', projectId)
      .eq('legacy_entry_id', logId)
      .limit(1);
    if (error || !Array.isArray(data)) return 'unklar';
    return data.length > 0 ? 'ja' : 'nein';
  } catch {
    return 'unklar';
  }
};

/**
 * Protokoll im Projekt-Blob auf dem Server — NUR der JSON-Pfad
 * `data->metadata->globalData->serviceLog` (wie retryJournalMirrors), ohne
 * loadProjectById und ohne den Sync-Anker. Kein Projekt sichtbar oder eine
 * andere Form als Liste → unklar; ein fehlendes Protokoll (`null`) heisst:
 * dort steht sicher nichts.
 */
const gutschriftImProtokoll = async (projectId: string, logId: string): Promise<QuellenBefund> => {
  try {
    const { data, error } = await supabase
      .from('projects')
      .select('log:data->metadata->globalData->serviceLog')
      .eq('id', projectId)
      .maybeSingle();
    if (error || !data || typeof data !== 'object' || !('log' in (data as object))) return 'unklar';
    const log = (data as { log?: unknown }).log;
    if (log === null) return 'nein';
    if (!Array.isArray(log)) return 'unklar';
    return log.some((e) => !!e && typeof e === 'object' && str((e as { id?: unknown }).id).trim() === logId) ? 'ja' : 'nein';
  } catch {
    return 'unklar';
  }
};

/**
 * Steht die Gutschrift `logId` (`gbu-zeit-<taetigkeitId>`) schon auf dem
 * Server? REIN LESEND, WIRFT NIE — fuer „Gutschrift nachholen" aus einem
 * womoeglich veralteten Fenster, das sonst doppelt schriebe.
 *
 * Zwei Quellen parallel: das Buchungsjournal (`service_journal`,
 * `legacy_entry_id`) und das Protokoll im Projekt-Blob (schmaler JSON-Pfad).
 * In einer gefunden → 'vorhanden'; in BEIDEN sicher nicht → 'fehlt'; jeder
 * Fehler, ein nicht sichtbares Projekt, eine unklare Form oder eine
 * unpassende Id → 'unbekannt' (dann NICHT nachholen).
 */
export const pruefeGutschriftAufServer = async (projectId: string, logId: string): Promise<GutschriftStand> => {
  try {
    const pid = (projectId || '').trim();
    const lid = (logId || '').trim();
    if (!pid || pid === 'temp-id' || !GBU_ZEIT_LOG_ID.test(lid)) return 'unbekannt';
    const [journal, protokoll] = await Promise.all([gutschriftImJournal(pid, lid), gutschriftImProtokoll(pid, lid)]);
    if (journal === 'ja' || protokoll === 'ja') return 'vorhanden';
    if (journal === 'nein' && protokoll === 'nein') return 'fehlt';
    return 'unbekannt';
  } catch {
    return 'unbekannt';
  }
};

/**
 * Vermerkt die Buchung auf einer LV-Position — GENAU EINMAL je Taetigkeit
 * (RPC `sifa_taetigkeit_gebucht_markieren`, Riegel `gebucht_am is null`,
 * Riegel e). Rueckgabe: die ids, die DIESER Aufruf tatsaechlich markiert hat
 * (`uuid[]`); fehlt eine uebergebene id darin, war sie schon gebucht — der
 * Aufrufer schreibt dann NUR die gemeldeten in die Leistungsliste. Eine Zahl
 * oder sonst etwas (aeltere SQL-Fassung) gilt als „nichts markiert". Fehlt
 * die RPC, wirft es mit Klartext — gebucht wird dann nicht.
 */
export const markiereTaetigkeitGebucht = async (ids: string[], positionId: string, menge: number): Promise<string[]> => {
  const liste = Array.from(new Set((ids ?? []).map((x) => (x || '').trim()).filter(Boolean)));
  if (liste.length === 0) return [];
  const pos = (positionId || '').trim();
  if (!pos) throw new Error('Keine LV-Position gewählt.');
  if (typeof menge !== 'number' || !Number.isFinite(menge) || menge <= 0) throw new Error('Die Menge muss größer als 0 sein.');

  const { data, error } = await supabase.rpc('sifa_taetigkeit_gebucht_markieren', {
    p_ids: liste,
    p_position_id: pos,
    p_menge: menge,
  });
  if (error) {
    if (istFehlendeRpc(error)) throw new Error(MELDUNG_RPC_FEHLT);
    return werfe(error);
  }
  if (!Array.isArray(data)) return [];
  const erlaubt = new Set(liste);
  return data.map((x) => str(x).trim()).filter((x) => x && erlaubt.has(x));
};
